import React, { useState, useRef, useEffect, useMemo, useCallback } from "react";
import CheckIcon from "@mui/icons-material/Check";
import {
  Drawer,
  Box,
  Typography,
  Card,
  CardContent,
  Chip,
  Divider,
  Fade,
  Collapse,
  CircularProgress,
} from "@mui/material";
import { motion, AnimatePresence } from "framer-motion";
import { MiscSwipeArea, ZoomableImage } from "./components/CaseMediaParts";
import DentalChart from "./components/DentalChart";
import portals from "./components/portals.js";
import {
  worktypeMap,
  getBackgroundImageSetForIconType,
  getHighestIconType,
  getWorktypeDisplayName,
  getBridgeUnitName,
  getMaterialName,
} from "./components/work-type-images.js";
import StlIcon from "./resources/icons/cad.svg";
import NextIcon from "./resources/icons/next.svg";
import PreviousIcon from "./resources/icons/previous.svg";
import PdfIcon from "./resources/icons/PdfIcon.svg";
import PlyIcon from "./resources/icons/3d-with-color-spark.svg";
import ContractIcon from "./resources/icons/contract.svg";
import ExpandIcon from "./resources/icons/expand_card.svg";
import CadmusIcon from "./resources/cadmus_light.svg";
import { openCaseSummaryPdf } from "./CaseSummaryPdf/openCaseSummaryPdf";
import WorktypeChips from "./WorktypeChips";
import apiClient from "./utils/apiClient";
import { formatDateTimestamp, formatDeliveryDate, normalizeJaw } from "./utils/caseFormat";
import { FEATURE_ACCEPT_ENABLED, FEATURE_CASE_SUMMARY_PDF, FEATURE_MISC_FILES_ENABLED, PDF_FLIP, PDF_FADE_WITH_DRAWER } from "./featureFlags";
import CloseIcon from "./components/CloseIcon";
import FileCategorySelector, { FileViewerControls, PdfPrintButton, PDF_PRINT_TOP_RIGHT } from "./components/FileCategorySelector";
import { usePdfCanvasViewer } from "./components/PdfCanvasViewer/usePdfCanvasViewer";
import { CASE_PREFETCH, updateCasePrefetch, clearCasePrefetch, initialScanLinks } from "./utils/casePrefetch";
import PdfCanvasViewer from "./components/PdfCanvasViewer/PdfCanvasViewer";
import { usePdfFlip } from "./components/PdfCanvasViewer/usePdfFlip";
import { useMobile } from "./hooks/useMobile";
import CaseDetailTouch from "./components/CaseDetailTouch";

const Draco = React.lazy(() => import("./components/Dracoloader"));
// Start het downloaden van de zware Dracoloader-chunk (three.js/gsap/drei)
// al zodra de drawer opent, i.p.v. pas wanneer <Draco> voor het eerst
// daadwerkelijk rendert — scheelt de merkbare vertraging bij de allereerste
// scan-weergave. Module-scope vlag zodat dit maar één keer per sessie gebeurt.
let dracoPrefetched = false;
function prefetchDraco() {
  if (dracoPrefetched) return;
  dracoPrefetched = true;
  import("./components/Dracoloader");
}

// Afstand van de bestands-/pdf-controls tot de onderkant, gelijk aan
// Front/Top/Side in Dracoloader. De viewers houden onderin ruimte vrij voor
// de controls (42px hoog) plus 16px lucht.
const FILE_CONTROLS_BOTTOM = 36;
const FILE_VIEWER_BOTTOM = FILE_CONTROLS_BOTTOM + 42 + 16;

// Laadvolgorde scans/IOS Form (elk los terug te zetten op false):
// - PREFETCH_ORDER_FORM_PDF: op de Scans-tab het pdf alvast op de achtergrond
//   laden zodra de scans binnen zijn, zodat IOS Form daarna direct klaarstaat.
// - ORDER_FORM_PDF_FIRST: op de IOS Form-tab nieuwe scan-downloads laten
//   wachten tot het pdf getekend is (zie holdLoading in Dracoloader).
const PREFETCH_ORDER_FORM_PDF = true;
const ORDER_FORM_PDF_FIRST = true;

// IOS Form-flip bij Next/Previous: zie usePdfFlip. PDF_FLIP en
// PDF_FADE_WITH_DRAWER (zonder flip, of als flip niet kan zoals bij reduced
// motion, fadet het IOS Form mee op het tempo van de kaart) staan in
// featureFlags.js.

// Bijlagen met deze extensies tonen een echte thumbnail; de rest (pdf, stl,
// zip, ...) krijgt een tegel met paperclip en de extensie.
const EMPTY_LIST = [];
const IMAGE_EXTENSIONS = ["jpg", "jpeg", "png", "gif", "webp", "bmp", "svg", "avif", "heic"];

// Thumbnail-kolom bij Files: maximaal THUMBS_MAX_VISIBLE thumbs volledig in
// beeld, de volgende fadet vanaf z'n helft naar 0, met een "+N"-badge voor
// wat er nog onder (of boven, na scrollen) zit. Op null = oude gedrag
// (kolom tot 60% van de hoogte, zonder fade/badge).
// Deel van de ruimte onder de kaartkop dat de notes minimaal krijgen als
// worktypes en notes samen niet passen (zie de verdeling in de meet-effect).
const NOTES_SHARE = 0.4;

const THUMBS_MAX_VISIBLE = 4;
const THUMB_SIZE = 80;
const THUMB_GAP = 8;
const THUMB_FADE = THUMB_SIZE / 2;

const thumbBadgeStyle = {
  position: "absolute",
  left: "50%",
  display: "inline-flex",
  padding: "4px 6px",
  alignItems: "center",
  gap: "4px",
  borderRadius: "4px",
  background: "rgba(255, 255, 255, 0.10)",
  color: "rgba(255, 255, 255, 0.40)",
  fontFamily: '"DM Sans", sans-serif',
  fontSize: "11px",
  fontStyle: "normal",
  fontWeight: 600,
  lineHeight: "16px",
  letterSpacing: "0.88px",
  textTransform: "uppercase",
  whiteSpace: "nowrap",
  pointerEvents: "none",
  transition: "opacity 0.2s ease",
};

// Scroll-kolom voor de thumbnails. De fade (mask) groeit mee met hoeveel er
// nog te scrollen valt, zodat hij niet verspringt aan het begin/eind, en de
// actieve thumb (bv. via de pijltjes in de toolbar) scrolt vanzelf in beeld.
function ThumbScroller({ count, activeIndex, ready, visible, children }) {
  const ref = useRef(null);
  const [m, setM] = useState({ st: 0, h: 0, max: 0 });
  const measure = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    const next = { st: el.scrollTop, h: el.clientHeight, max: Math.max(0, el.scrollHeight - el.clientHeight) };
    setM((p) => (p.st === next.st && p.h === next.h && p.max === next.max ? p : next));
  }, []);
  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [measure, count, ready]);

  const fadeTop = Math.min(THUMB_FADE, m.st);
  const fadeBottom = Math.min(THUMB_FADE, m.max - m.st);
  const pitch = THUMB_SIZE + THUMB_GAP;
  // Alleen thumbs die helemaal buiten beeld zijn tellen mee; de half
  // zichtbare (fadende) thumb zie je al.
  const above = ready && fadeTop > 1 ? Math.min(count, Math.max(0, Math.floor((m.st - THUMB_SIZE) / pitch) + 1)) : 0;
  const below = ready && fadeBottom > 1 ? Math.max(0, count - Math.ceil((m.st + m.h) / pitch)) : 0;
  // Tijdens het uitfaden het laatste getal vasthouden, anders zie je even "+0"
  const lastAbove = useRef(1);
  const lastBelow = useRef(1);
  if (above > 0) lastAbove.current = above;
  if (below > 0) lastBelow.current = below;

  // Actieve thumb binnen het heldere (niet-gefade) deel houden
  useEffect(() => {
    const el = ref.current;
    if (!el || !ready || activeIndex < 0) return;
    const top = activeIndex * pitch;
    const bottom = top + THUMB_SIZE;
    const h = el.clientHeight;
    const max = el.scrollHeight - h;
    let target = null;
    if (top < el.scrollTop + (el.scrollTop > 0 ? THUMB_FADE : 0)) target = top - THUMB_FADE;
    else if (bottom > el.scrollTop + h - (el.scrollTop < max ? THUMB_FADE : 0)) target = bottom - h + THUMB_FADE;
    if (target != null) el.scrollTo({ top: Math.max(0, Math.min(max, target)), behavior: "smooth" });
  }, [activeIndex, ready, pitch]);

  const mask = `linear-gradient(to bottom, transparent 0px, #000 ${fadeTop}px, #000 calc(100% - ${fadeBottom}px), transparent 100%)`;

  return (
    <Box sx={{ position: "relative" }}>
      <Box
        ref={ref}
        onScroll={measure}
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: `${THUMB_GAP}px`,
          maxHeight: `min(60vh, ${THUMBS_MAX_VISIBLE * pitch + THUMB_SIZE}px)`,
          overflowY: "auto",
          scrollbarWidth: "none",
          msOverflowStyle: "none",
          "&::-webkit-scrollbar": { display: "none" },
          maskImage: mask,
          WebkitMaskImage: mask,
        }}
      >
        {children}
      </Box>
      <span style={{ ...thumbBadgeStyle, top: 0, transform: "translate(-50%, -100%)", opacity: visible && above > 0 ? 1 : 0 }}>
        +{lastAbove.current}
      </span>
      <span style={{ ...thumbBadgeStyle, bottom: 0, transform: "translate(-50%, 100%)", opacity: visible && below > 0 ? 1 : 0 }}>
        +{lastBelow.current}
      </span>
    </Box>
  );
}

// Bevriest `value` zolang `ready` false is (blijft de laatst-klare waarde
// teruggeven i.p.v. elke tussentijdse/nog-niet-correcte waarde door te
// laten). Gebruikt voor de PDF-toolbar: zonder dit knippert de zoom/fit-
// weergave heel even naar een verkeerde tussenstand (bv. terug naar "100%")
// bij elke nieuwe case/document, vlak vóórdat de echte fit berekend is - en
// omdat de toolbar bewust geen breedte-animatie meer heeft (voor
// soepelheid), was die korte foute waarde ook meteen zichtbaar als een hik
// in de breedte van de control.
function useFrozenUntilReady(value, ready) {
  const frozenRef = useRef(value);
  if (ready) frozenRef.current = value;
  return frozenRef.current;
}

export default function CaseDetailDrawer({
  open,
  rowData,
  onClose,
  onOlderRow,
  onNewerRow,
  neighborRows = null,
  index = 0,
  allRowsLength = 0,
  pendingNewRows = [],
  isLoading = false,
  onDownloadPdf,
  isDownloading,
  onDownloadZipWithPdf,
  onDownloadStlZip,
  onUpdateRow,
}) {
  // Telefoon/iPad staand: eigen fullscreen layout (CaseDetailTouch), altijd
  // "expanded" (worktypes, tand-plaatjes en notes zijn daar altijd te zien)
  const { isCompact } = useMobile();
  // Met de eigen PDF-bon-knop (FEATURE_CASE_SUMMARY_PDF) schuiven PDF/STL/PLY
  // in de uitgeklapte kaart één plek naar links, zodat de rij rechts blijft
  // uitlijnen met de tandboog.
  const caseSummaryShift = FEATURE_CASE_SUMMARY_PDF() ? 52 : 0;
  const [expandedPref, setExpanded] = useState(() => {
    const saved = localStorage.getItem("viewer_expanded");
    return saved !== null ? saved === "true" : true;
  });
  const expanded = isCompact || expandedPref;
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [isContentLoading, setIsContentLoading] = useState(false);
  const chartRef = useRef(null);
  const navTimeoutRef = useRef(null); // Prevent overlapping nav timers
  const [highlightPreviews, setHighlightPreviews] = useState({});
  const [awaitingNext, setAwaitingNext] = useState(false);
  const [localStatus, setLocalStatus] = useState(rowData?.status ?? 'new');
  const [acceptHovered, setAcceptHovered] = useState(false);
  const acceptHoverTimer = useRef(null);
  useEffect(() => { setLocalStatus(rowData?.status ?? 'new'); }, [rowData?.id]);
  useEffect(() => { if (open) prefetchDraco(); }, [open]);
  const cardRef = useRef(null); // Ref voor de Card om hoogte te meten
  const [drawerHeight, setDrawerHeight] = useState(0); // Dynamische hoogte
  const EMPTY_PIXEL =
    "data:image/gif;base64,R0lGODlhAQABAAD/ACwAAAAAAQABAAACADs=";
  // Fade durations as variables, adjustable based on expanded state
  const fadeDuration = "0.2s";
  const fadeTimingFunction = "ease-in-out";

  const pendingNextRef = useRef(false);
  const keyNavRef = useRef({});
  const [flashPrev, setFlashPrev] = useState(false);
  const [flashNext, setFlashNext] = useState(false);
  const [measureOverlay, setMeasureOverlay] = useState(null); // { top, height }
  const notesHeaderRef = useRef(null);
  const notesContentRef = useRef(null);

  // Memoized organization of jaw-based, bridges and singles
  const { jawBased, bridges, singles } = useMemo(() => {
    if (!rowData || !highlightPreviews) {
      return { jawBased: [], bridges: [], singles: [] };
    }

    const organizeAllUnits = () => {
      const jawBased = [];
      const bridges = [];
      const bridgeWorktypeMap = new Map();
      const singles = [];

      // First, collect jaw-based worktypes (full jaw restorations)
      rowData.worktypes?.forEach((worktype) => {
        // Normalize FDI for ranges/lists
        if (
          typeof worktype.FDI === "string" &&
          (worktype.FDI.includes("-") || worktype.FDI.includes(","))
        ) {
          worktype.FDI = "";
        }

        // ✅ CHECK: If Jaw is filled, it's ALWAYS jaw-based (ignore FDI)
        if (worktype.Jaw) {
          jawBased.push({
            worktype,
            jaw: normalizeJaw(worktype.Jaw),
          });
          // Mark this worktype as processed so we skip it later
          worktype._isJawBased = true;
          worktype.FDI = "";
        }
      });

      // Sort jaw-based: groups with an upper-jaw item come first, same types stay adjacent,
      // and within a group upper always precedes lower.
      const typeOrder = { Model: 1, Splint: 2, SurgicalGuide: 3, Denture: 4 };
      const jawOrder = { Upper: 1, Lower: 2, Both: 3 };

      // Determine the best (lowest) jaw rank present in each type group so that
      // a group containing an upper-jaw item sorts before a group that only has lower.
      const typeGroupJawRank = {};
      jawBased.forEach(item => {
        const type = item.worktype.RestorationTypeID;
        const jr = jawOrder[item.jaw] ?? 999;
        typeGroupJawRank[type] = Math.min(typeGroupJawRank[type] ?? 999, jr);
      });

      jawBased.sort((a, b) => {
        const typeA = a.worktype.RestorationTypeID;
        const typeB = b.worktype.RestorationTypeID;

        // 1. Group with upper jaw comes before group without
        const groupJawA = typeGroupJawRank[typeA] ?? 999;
        const groupJawB = typeGroupJawRank[typeB] ?? 999;
        if (groupJawA !== groupJawB) return groupJawA - groupJawB;

        // 2. Within same group-jaw-rank: keep type groups together
        const tOrdA = typeOrder[typeA] ?? 999;
        const tOrdB = typeOrder[typeB] ?? 999;
        if (tOrdA !== tOrdB) return tOrdA - tOrdB;

        // 3. Within the same type group: upper before lower
        const jawA = jawOrder[a.jaw] ?? 999;
        const jawB = jawOrder[b.jaw] ?? 999;
        return jawA - jawB;
      });

      // Then, identify all bridges from rowData.worktypes
      rowData.worktypes?.forEach((worktype) => {
        // ✅ Skip if already processed as jaw-based
        if (worktype._isJawBased) {
          return;
        }

        if (
          worktype?.Units &&
          Array.isArray(worktype.Units) &&
          worktype.Units.length > 0
        ) {
          // This is a bridge - collect all its units
          const unitFdis = worktype.Units.map((u) => Number(u.FDI))
            .filter((fdi) => !isNaN(fdi))
            .sort((a, b) => a - b);

          const bridgeUnits = worktype.Units.map((unit) => ({
            num: Number(unit.FDI),
            preview: highlightPreviews[Number(unit.FDI)] || {
              dataUrl: "",
              worktype: unit,
            },
          }));

          bridges.push({
            worktype,
            units: bridgeUnits,
            fromTo:
              unitFdis.length > 0
                ? `${worktype.From} - ${worktype.To}`
                : "Bridge",
          });

          // Mark all unit FDIs as belonging to a bridge
          unitFdis.forEach((fdi) => bridgeWorktypeMap.set(fdi, true));
        }
      });

      // Finally, collect single units from highlightPreviews that are NOT part of a bridge
      Object.entries(highlightPreviews).forEach(([num, preview]) => {
        const numKey = Number(num);

        // Skip if this is part of a bridge
        if (bridgeWorktypeMap.has(numKey)) {
          return;
        }

        // Check if this is a single unit (no Units array)
        const worktype = preview?.worktype;
        if (
          !worktype?.Units ||
          !Array.isArray(worktype.Units) ||
          worktype.Units.length === 0
        ) {
          // ✅ Skip if this is a jaw-based worktype
          if (worktype?.Jaw || worktype?._isJawBased) {
            return;
          }

          singles.push({
            num,
            preview,
            worktype,
          });
        }
      });

      console.log("Organized units:", {
        jawBasedCount: jawBased.length,
        bridgesCount: bridges.length,
        singlesCount: singles.length,
      });

      return { jawBased, bridges, singles };
    };

    return organizeAllUnits();
  }, [rowData, highlightPreviews]);

  const totalWorktypes = useMemo(() => {
    return (
      jawBased.length +
      bridges.length +
      bridges.reduce((sum, b) => sum + (b.units?.length || 0), 0) +
      singles.length
    );
  }, [jawBased, bridges, singles]);

  const [wtAllocated, setWtAllocated] = useState(null);
  const [notesAllocated, setNotesAllocated] = useState(null);
  const dynamicMinHeight = "0px";
  const dynamicMaxHeight = wtAllocated != null ? `${wtAllocated}px` : "180px";

  const generateDentalHighlights = (worktypes) => {
    const highlights = {};

    const TOOTH_HIGHLIGHT  = "rgba(255, 255, 255, 0.8)";
    const PONTIC_HIGHLIGHT = "rgba(255, 255, 255, 0.45)";
    const JAW_HIGHLIGHT    = "rgba(255, 255, 255, 0.8)";

    if (!Array.isArray(worktypes)) return highlights;

    worktypes.forEach((worktype) => {
      if (!worktype) return;

      // ✅ CHECK: If Jaw is filled, it's ALWAYS jaw-based (ignore FDI completely)
      if (worktype.Jaw) {
        const jawValue = normalizeJaw(worktype.Jaw);

        if (jawValue === "Both") {
          highlights.Upper = JAW_HIGHLIGHT;
          highlights.Lower = JAW_HIGHLIGHT;
        } else if (jawValue) {
          // Normal single jaw
          highlights[jawValue] = JAW_HIGHLIGHT;
        }
        // ⚠️ SKIP FDI processing for this worktype - return early
        return;
      }

      // 1. Single tooth (FDI) - only if NO Jaw field
      if (worktype.FDI != null) {
        const fdi = parseInt(worktype.FDI, 10);
        if (fdi >= 11 && fdi <= 85) {
          highlights[fdi] = TOOTH_HIGHLIGHT;
        }
      }

      // 2. Units array (multiple teeth) - only if NO Jaw field
      if (Array.isArray(worktype.Units)) {
        worktype.Units.forEach((unit) => {
          if (unit?.FDI != null) {
            const fdi = parseInt(unit.FDI, 10);
            if (fdi >= 11 && fdi <= 85) {
              const isPontic = unit.RestorationDetailID === "Pontic";
              // Don't downgrade a tooth that's already highlighted as a full unit
              if (!highlights[fdi] || highlights[fdi] === PONTIC_HIGHLIGHT) {
                highlights[fdi] = isPontic ? PONTIC_HIGHLIGHT : TOOTH_HIGHLIGHT;
              }
            }
          }
        });
      }
    });

    return highlights;
  };

  // Voor loadHighlightPreviews (array)
  const getDentalHighlightsList = (worktypes) => {
    const highlights = [];
    worktypes?.forEach((worktype) => {
      if (worktype?.FDI) {
        highlights.push(Number(worktype.FDI));
      } else if (worktype?.Jaw) {
        const jawKey =
          worktype.Jaw.charAt(0).toUpperCase() + worktype.Jaw.slice(1);
        highlights.push(jawKey);
      }
      // Check voor Units array
      if (worktype?.Units && Array.isArray(worktype.Units)) {
        worktype.Units.forEach((unit) => {
          if (unit?.FDI) {
            highlights.push(Number(unit.FDI));
          }
        });
      }
    });
    return highlights;
  };
  const loadHighlightPreviews = () => {
    if (!rowData || !expanded || !chartRef.current) {
      console.log(
        "loadHighlightPreviews skipped: expanded=",
        expanded,
        "rowData=",
        !!rowData,
        "ref=",
        !!chartRef.current,
      );
      setIsContentLoading(false);
      if (isTransitioning) {
        setIsTransitioning(false);
      }
      return;
    }
    const highlights = getDentalHighlightsList(rowData.worktypes);
    const newPreviews = {};
    const worktypeMap = {};
    rowData.worktypes?.forEach((worktype) => {
      if (worktype?.FDI) {
        worktypeMap[Number(worktype.FDI)] = worktype;
      } else if (worktype?.Jaw) {
        const jawKey =
          worktype.Jaw.charAt(0).toUpperCase() + worktype.Jaw.slice(1);
        worktypeMap[jawKey] = worktype;
      }
      // Check Units
      if (worktype?.Units && Array.isArray(worktype.Units)) {
        worktype.Units.forEach((unit) => {
          if (unit?.FDI) {
            worktypeMap[Number(unit.FDI)] = unit;
          }
        });
      }
    });
    highlights.forEach((num) => {
      const svgData = chartRef.current.getToothSVG(num);
      if (svgData) {
        newPreviews[num] = {
          dataUrl: svgData.dataUrl,
          worktype: worktypeMap[num] || null,
        };
      }
    });

    setHighlightPreviews(newPreviews);
    setIsContentLoading(false);
    if (isTransitioning) {
      setIsTransitioning(false);
    }

    // Return false only when tooth (numeric) highlights were expected but none
    // loaded — this means DentalChart is mounted but SVGs not rendered yet.
    // Jaw-based highlights use string keys ("Upper"/"Lower") and getToothSVG
    // may legitimately return null for those, so we exclude them from the check.
    const toothHighlights = highlights.filter((h) => typeof h === "number");
    return !(toothHighlights.length > 0 && Object.keys(newPreviews).length === 0);
  };

  useEffect(() => {
    if (!isLoading && pendingNextRef.current) {
      pendingNextRef.current = false;
      setAwaitingNext(false);
      onOlderRow(index);
    }
  }, [isLoading]);

  useEffect(() => {
    pendingNextRef.current = false;
  }, [rowData?.id]);

  useEffect(() => {
    if (expanded && open && cardRef.current) {
      const measureHeight = () => {
        const height = cardRef.current.offsetHeight || window.innerHeight - 16;
        setDrawerHeight(height);
      };

      const expandTimer = setTimeout(measureHeight, 400);

      // ← VOEG DIT TOE
      let resizeTimeout;
      const handleResize = () => {
        clearTimeout(resizeTimeout);
        resizeTimeout = setTimeout(measureHeight, 200);
      };
      window.addEventListener("resize", handleResize);

      return () => {
        clearTimeout(expandTimer);
        window.removeEventListener("resize", handleResize); // ← EN DIT
        clearTimeout(resizeTimeout);
      };
    }
  }, [expanded, open]);

  // Separate effect for expanded re-measure
  useEffect(() => {
    if (expanded && open && cardRef.current) {
      const expandTimer = setTimeout(() => {
        const height = cardRef.current.offsetHeight || window.innerHeight - 16;
        setDrawerHeight(height);
      }, 400); // Langer wachten op transitie
      return () => clearTimeout(expandTimer);
    }
  }, [expanded, open]);

  useEffect(() => {
    if (expanded && rowData) {
      setIsContentLoading(true);
      let attempts = 0;
      const maxAttempts = 10;
      const tryLoad = () => {
        if (chartRef.current && !isTransitioning) {
          const done = loadHighlightPreviews();
          // done===false means chart is mounted but SVGs not ready yet — retry
          if (done === false && attempts < maxAttempts) {
            attempts++;
            setTimeout(tryLoad, 100);
          }
        } else if (attempts < maxAttempts && !isTransitioning) {
          attempts++;
          setTimeout(tryLoad, 100);
        }
      };
      const timer = setTimeout(tryLoad, 100);
      return () => clearTimeout(timer);
    }
    // REMOVED: else if (!expanded) { clear }
  }, [expanded, rowData?.id, isTransitioning]);

  // At the TOP of your component, before any conditional returns
  // Direct uit rowData afgeleid (useMemo, niet via een effect + state): zo
  // horen de scans die Dracoloader krijgt altijd bij dezelfde render als
  // rowData/caseKey — met een effect liep viewerData één render achter, en
  // hoorde de nieuwe case heel even bij de scan-data van de vorige.
  const { viewerData, noScans } = useMemo(() => {
    if (rowData?.stlfiles) {
      try {
        const parsedFiles = JSON.parse(rowData.stlfiles);
        if (Array.isArray(parsedFiles)) {
          if (parsedFiles.length === 0) {
            return { viewerData: [], noScans: true };
          }
          // Filter preview files and group by type
          const previewFiles = parsedFiles.filter(
            (file) => file.fileType === "preview",
          );

          // Build viewer data as an array of preview entries so multiple files
          // of the same `type` + `jaw` are preserved instead of being merged.
          const viewerArray = previewFiles.map((file, idx) => ({
            type: file.type,
            upper: file.jaw === "upper" ? file.fileLink : null,
            lower: file.jaw === "lower" ? file.fileLink : null,
            // unique id to allow multiple entries with same `type`
            id: `${file.type}-${file.jaw}-${idx}`,
          }));

          return { viewerData: viewerArray, noScans: false };
        } else {
          console.warn("Parsed stlfiles is not an array");
          return { viewerData: [], noScans: false };
        }
      } catch (error) {
        console.error("Failed to parse stlfiles JSON:", error);
        return { viewerData: [], noScans: false };
      }
    }
    return { viewerData: [], noScans: false };
  }, [rowData]);

  // Welke content het hoofdgedeelte toont: scans (3D), het order-form pdf, of
  // de overige bijlagen. Scans/pdf-keuze blijft staan tussen cases; Files
  // reset altijd terug naar scans zodra je naar een andere case gaat (zie de
  // case-wissel-effect verderop).
  const [fileCategory, setFileCategory] = useState("scans");
  // Forceert een remount van FileCategorySelector wanneer fileCategory
  // programmatisch (niet door een klik) teruggezet wordt naar "scans" — zie
  // de case-wissel-effect verderop.
  const [selectorKey, setSelectorKey] = useState(0);
  // Color/mono was voorheen Dracoloader's eigen state; nu hier zodat de
  // FileCategorySelector 'm boven alle drie de categorieën kan tonen.
  const [scanMode, setScanMode] = useState(() => {
    const saved = localStorage.getItem("viewer_vertex_colors");
    return saved !== null ? (saved === "true" ? "color" : "mono") : "color";
  });
  const handleScanModeChange = useCallback((m) => {
    setScanMode(m);
    localStorage.setItem("viewer_vertex_colors", String(m === "color"));
  }, []);

  // Overige (niet-scan) bijlagen: foto's/PDF's uit accepted_data.miscellaneousFiles
  const [miscFiles, setMiscFiles] = useState([]);
  const [selectedMiscFile, setSelectedMiscFile] = useState(null);
  // Per bestand bijhouden (niet één globale vlag!) — anders blokkeert een
  // mislukte fetch van bv. een thumbnail voor bestand B ook de hoofdweergave
  // van bestand A, en blijft die melding hangen zodra je heen en weer
  // schakelt tussen Files/PDF/Scans zonder dat selectedMiscFile zelf wijzigt.
  const [miscErrorIds, setMiscErrorIds] = useState(() => new Set());
  const markMiscError = useCallback((id) => {
    setMiscErrorIds((prev) => (prev.has(id) ? prev : new Set(prev).add(id)));
  }, []);
  // Het bestand dat *getoond* wordt — volgt selectedMiscFile onmiddellijk (dus
  // ook meteen bij het loslaten van een swipe). Blijft ook bestaan nadat
  // selectedMiscFile weer null wordt (dicht), zodat de container tijdens het
  // wegfaden nog iets toont. De slide-transitie start hier meteen op — de
  // content (img/iframe) rendert direct mee zodra de url er is, in plaats van
  // te wachten tot 'ie volledig gedecodeerd/geladen is. Anders schuift er een
  // lege frame naar binnen en "pop't" de content er pas ná de slide bij (zag
  // eruit als "staat er al, scrollt dan nog een stukje").
  const [displayMiscFile, setDisplayMiscFile] = useState(null);
  // Zelfde bevries-truc voor de thumbnail-lijst zelf: zonder dit toont de
  // sidebar tijdens het dichtfaden alvast de (al bijgewerkte) miscFiles van
  // de NIEUWE case, waardoor de thumbs abrupt verspringen/verdwijnen in
  // plaats van rustig mee te faden met de rest van de preview.
  const [displayMiscFiles, setDisplayMiscFiles] = useState([]);
  useEffect(() => {
    if (selectedMiscFile) {
      setDisplayMiscFile(selectedMiscFile);
      setDisplayMiscFiles(miscFiles);
    }
  }, [selectedMiscFile, miscFiles]);
  // Niet meer gekoppeld aan of er al een bestand geselecteerd is (dat deed
  // vroeger een losse "Misc Files"-knop) — de categorie zelf is nu leidend:
  // zodra je de "Files"-tab kiest, moet de misc-preview meteen zichtbaar zijn.
  const isMiscOpen = fileCategory === "misc-files";
  const isScansActive = fileCategory === "scans";
  const isOrderFormActive = fileCategory === "order-form";
  // Wat er van Files getoond mag worden: uitsluitend bestanden van de huidige
  // case. displayMiscFile(s) blijft na een case-wissel nog even staan (en
  // ids/urls zijn per bestand gecachet); dit voorkomt dat er ooit — ook niet
  // één frame of tijdens een fade — een bijlage van een andere patiënt in
  // beeld komt. Bij een case-wissel verdwijnt de bijlage dus direct.
  const currentCaseId = rowData?.id ?? null;
  const shownMiscFile = displayMiscFile && displayMiscFile.caseId === currentCaseId ? displayMiscFile : null;
  const shownMiscFiles = displayMiscFiles.length && displayMiscFiles[0].caseId === currentCaseId ? displayMiscFiles : EMPTY_LIST;

  // Zodra je op "Files" klikt en er nog niets geselecteerd is, meteen het
  // eerste bestand tonen — er is geen aparte "open"-klik meer nodig.
  useEffect(() => {
    if (isMiscOpen && !selectedMiscFile && miscFiles.length > 0) {
      setSelectedMiscFile(miscFiles[0]);
    }
  }, [isMiscOpen, selectedMiscFile, miscFiles]);

  useEffect(() => {
    if (rowData?.miscfiles) {
      try {
        const parsed = JSON.parse(rowData.miscfiles);
        if (Array.isArray(parsed) && parsed.length > 0) {
          // Case-id in elk bestand (en in de id): urls/laadstatus worden per
          // id bewaard, en "misc-0" van de vorige case mag nooit voor die
          // van deze case aangezien worden.
          const caseId = rowData?.id ?? null;
          const arr = parsed
            .map((f, idx) => {
              // miscellaneousFiles kan zowel kale GCS-paden (strings) als
              // objecten ({ fileLink, filename, ... }) bevatten.
              const fileLink =
                typeof f === "string" ? f : f?.fileLink || f?.filelink || f?.path || "";
              const filename =
                (typeof f === "object" && (f?.filename || f?.name)) ||
                fileLink.split("/").pop() ||
                `File ${idx + 1}`;
              const ext = filename.includes(".") ? filename.split(".").pop().toLowerCase() : "";
              return {
                id: `misc-${caseId}-${idx}`, caseId, filename, fileLink, ext,
                isPdf: ext === "pdf",
                isImage: IMAGE_EXTENSIONS.includes(ext),
              };
            })
            .filter((f) => f.fileLink);
          setMiscFiles(arr);
        } else {
          setMiscFiles([]);
        }
      } catch (error) {
        console.error("Failed to parse miscellaneousFiles JSON:", error);
        setMiscFiles([]);
      }
    } else {
      setMiscFiles([]);
    }
  }, [rowData]);

  // Bij het wisselen van case moet een eventueel geopende preview weer dicht.
  // Alleen selectedMiscFile meteen op null zetten (dat triggert de bestaande
  // opacity/scale-fade via isMiscOpen) — displayMiscFile pas ná die fade
  // opruimen, anders klapt de hele preview (incl. thumbnail-sidebar) meteen
  // weg zonder animatie, in tegenstelling tot hoe de scan-stacks wél faden.
  // caseSessionRef gaat wél meteen omhoog: een nog lopende fetch van vóór het
  // wisselen mag straks niet alsnog in de cache van de NIEUWE case schrijven
  // (ids zijn simpel misc-0/misc-1/... en kunnen dus tussen cases botsen).
  const caseSessionRef = useRef(0);
  useEffect(() => {
    caseSessionRef.current += 1;
    // Files reset altijd terug naar scans bij een case-wissel; scans/pdf
    // blijven staan zoals ze waren. FileCategorySelector onthoudt zijn actieve
    // tab intern (alleen bij mount vanuit props) — bij zo'n reset moet 'ie dus
    // geforceerd remounten (via key) om visueel weer "Scans" te tonen.
    if (fileCategory === "misc-files") {
      setFileCategory("scans");
      setSelectorKey((k) => k + 1);
    }
    setSelectedMiscFile(null);
    // Alleen opruimen wat bij een ándere case hoort: wie binnen deze 400 ms
    // alweer Files opent, heeft intussen al bestanden van déze case geladen
    // (die moeten blijven, anders blijft de weergave leeg).
    const caseId = rowData?.id ?? null;
    const isCurrent = (id) => String(id).startsWith(`misc-${caseId}-`);
    const keepCurrentIds = (set) => new Set([...set].filter(isCurrent));
    const t = setTimeout(() => {
      setDisplayMiscFile((prev) => (prev && prev.caseId !== caseId ? null : prev));
      setDisplayMiscFiles((prev) => (prev.length && prev[0].caseId !== caseId ? [] : prev));
      setMiscUrls((prev) => Object.fromEntries(Object.entries(prev).filter(([id]) => isCurrent(id))));
      miscFetchedIdsRef.current = keepCurrentIds(miscFetchedIdsRef.current);
      setLoadedMiscIds(keepCurrentIds);
      setMiscErrorIds(keepCurrentIds);
      miscPreloadStartedRef.current = keepCurrentIds(miscPreloadStartedRef.current);
    }, 400);
    return () => clearTimeout(t);
  }, [rowData?.id]);

  // Richting van de laatste navigatie (voor de slide-transitie bij next/prev).
  const [miscNavDirection, setMiscNavDirection] = useState(1);

  // Eén gedeelde cache voor alle signed urls (zowel de hoofdweergave als de
  // thumbnails) — zo wordt een file maar één keer gedownload: de thumbnail
  // gebruikt exact dezelfde url als de volledige weergave, dus de browser
  // hergebruikt die download i.p.v. 'm nog een keer op te halen. Ook: elk
  // bestand heeft zijn eigen slot, dus id/url kunnen nooit door elkaar raken
  // bij snel navigeren.
  const [miscUrls, setMiscUrls] = useState({});
  const miscFetchedIdsRef = useRef(new Set());
  // Aparte "echt geladen"-state: een signed url ophalen zegt niks over of de
  // browser de <img> ook daadwerkelijk gedownload/gedecodeerd heeft. Zonder
  // dit onderscheid start de stagger-animatie zodra de url binnen is, terwijl
  // de thumbnail zelf nog leeg is — en plopt het echte plaatje er later,
  // zonder eigen transitie, willekeurig doorheen.
  const [loadedMiscIds, setLoadedMiscIds] = useState(() => new Set());
  const markMiscLoaded = useCallback((id) => {
    setLoadedMiscIds((prev) => (prev.has(id) ? prev : new Set(prev).add(id)));
  }, []);

  const fetchMiscUrl = useCallback((file) => {
    if (!file || miscFetchedIdsRef.current.has(file.id)) return;
    miscFetchedIdsRef.current.add(file.id);
    const session = caseSessionRef.current;
    apiClient
      .post("/files/signed-url", {
        fileLink: file.fileLink,
        organizationId: parseInt(localStorage.getItem("organization_id")),
      })
      .then((res) => {
        if (caseSessionRef.current !== session) return;
        const url = res.data?.url || null;
        if (!url) {
          markMiscError(file.id);
          return;
        }
        setMiscUrls((prev) => ({ ...prev, [file.id]: url }));
      })
      .catch((error) => {
        if (caseSessionRef.current !== session) return;
        console.error("Failed to load miscellaneous file preview:", error);
        markMiscError(file.id);
      });
  }, [markMiscError]);

  // Hoofdweergave: haal het geselecteerde bestand op. PDF's slaan dit over —
  // die lopen via de /files/proxy-route van PdfCanvasViewer (zie miscPdfViewer
  // hieronder), een signed url is voor hen niet nodig.
  useEffect(() => {
    if (!selectedMiscFile || selectedMiscFile.isPdf) return;
    fetchMiscUrl(selectedMiscFile);
  }, [selectedMiscFile, fetchMiscUrl]);

  // Thumbnails: haal ze allemaal (geen pdf's, die krijgen een generiek icoon)
  // op zodra de preview open staat — via dezelfde cache/functie hierboven.
  useEffect(() => {
    if (!isMiscOpen) return;
    miscFiles.filter((f) => f.isImage).forEach(fetchMiscUrl);
  }, [isMiscOpen, miscFiles, fetchMiscUrl]);

  // Onzichtbare preload zodra een url binnen is: dit is de ENIGE manier
  // waarop loadedMiscIds voor de THUMBNAILS gevuld raakt. De zichtbare
  // thumbnail-<img>'s worden pas gerenderd zodra allThumbsReady al true is —
  // zonder deze losse, headless new Image() zou dat een deadlock zijn (de
  // dingen die "geladen" moeten melden bestaan dan nog niet in de DOM).
  const miscPreloadStartedRef = useRef(new Set());
  useEffect(() => {
    Object.entries(miscUrls).forEach(([id, url]) => {
      if (miscPreloadStartedRef.current.has(id)) return;
      miscPreloadStartedRef.current.add(id);
      const img = new window.Image();
      img.onload = () => markMiscLoaded(id);
      img.onerror = () => markMiscLoaded(id);
      img.src = url;
    });
  }, [miscUrls, markMiscLoaded]);

  const goToMiscOffset = useCallback(
    (delta) => {
      if (!selectedMiscFile || miscFiles.length === 0) return;
      const currentIndex = miscFiles.findIndex((f) => f.id === selectedMiscFile.id);
      if (currentIndex === -1) return;
      const nextIndex = (currentIndex + delta + miscFiles.length) % miscFiles.length;
      setMiscNavDirection(delta >= 0 ? 1 : -1);
      setSelectedMiscFile(miscFiles[nextIndex]);
    },
    [selectedMiscFile, miscFiles],
  );

  // Swipe/drag door de misc-preview heen (touch én muis — framer-motion's
  // drag gebruikt in beide gevallen dezelfde Pointer Events API). "Power" =
  // afstand * snelheid, zodat zowel een trage-maar-verre sleep als een korte-
  // maar-snelle flick als swipe tellen.
  const handleMiscSwipe = useCallback(
    (_event, { offset, velocity }) => {
      const power = Math.abs(offset.x) * velocity.x;
      if (power < -10000) goToMiscOffset(1);
      else if (power > 10000) goToMiscOffset(-1);
    },
    [goToMiscOffset],
  );

  // Pas als ALLE thumbnails klaar zijn (pdf's tellen meteen mee, die hebben
  // geen fetch nodig) laten we ze in één keer gestaggerd infaden — tot die
  // tijd toont de sidebar gewoon één spinner, in plaats van dat elk plaatje
  // los pop't zodra het toevallig klaar is.
  const allThumbsReady = useMemo(
    () => shownMiscFiles.every((f) => !f.isImage || loadedMiscIds.has(f.id)),
    [shownMiscFiles, loadedMiscIds],
  );

  const selectMiscFile = useCallback(
    (file) => {
      const fromIndex = selectedMiscFile
        ? miscFiles.findIndex((f) => f.id === selectedMiscFile.id)
        : -1;
      const toIndex = miscFiles.findIndex((f) => f.id === file.id);
      setMiscNavDirection(toIndex >= fromIndex ? 1 : -1);
      setSelectedMiscFile(file);
    },
    [selectedMiscFile, miscFiles],
  );

  // Voor de bestandsnavigatie (←/→) in de FileCategorySelector-toolbar: index
  // van het GETOONDE bestand (displayMiscFile/displayMiscFiles, niet de rauwe
  // selectedMiscFile/miscFiles) — zelfde bron als de hoofdweergave hieronder,
  // zodat de toolbar nooit even een ander bestand/aantal toont dan wat er nu
  // daadwerkelijk in beeld staat tijdens een wissel.
  const miscFileIndex = shownMiscFile
    ? shownMiscFiles.findIndex((f) => f.id === shownMiscFile.id)
    : -1;

  // Order-form pdf ("PDF"-tab): accepted_data.orderForm.fileLink, hier
  // rowData.orderform. De PDF zelf loopt via orderFormPdfViewer (onze eigen
  // /files/proxy-route hieronder, direct op basis van rowData.orderform) —
  // geen aparte signed-url-fetch meer nodig. Die bestond nog van vóór de
  // proxy-route, en zorgde bij elke case-wissel voor een extra, trage
  // round-trip die de PDF nodeloos liet wachten.
  const orderFormMissing = isOrderFormActive && !rowData?.orderform;

  // Loopt bewust via onze eigen /files/proxy-route i.p.v. de al-opgehaalde
  // signed GCS-url (orderFormUrl / miscUrls[...]): pdf.js downloadt de PDF
  // via fetch(), en storage.googleapis.com stuurt geen CORS-headers mee voor
  // dit origin — dat gaf "Failed to fetch". Onze eigen backend heeft al wel
  // CORS voor dit frontend-origin geconfigureerd (zie de proxy-route zelf).
  const buildPdfProxyUrl = (fileLink) => {
    if (!fileLink) return null;
    const organizationId = localStorage.getItem("organization_id") || "";
    return `${apiClient.defaults.baseURL}/files/proxy?fileLink=${encodeURIComponent(fileLink)}&organizationId=${encodeURIComponent(organizationId)}`;
  };

  // TWEE aparte viewer-instanties (niet één gedeelde): de order-form-Box
  // blijft altijd gemount (fade i.p.v. unmount, voor de case-switch-transitie)
  // en de misc-files-Box blijft ook gemount zolang displayMiscFile gezet is
  // (niet alleen wanneer die tab actief is) — ze zijn dus nooit tegelijk
  // zíchtbaar, maar wél vaak tegelijk in de DOM. Met één gedeelde hook-
  // instantie (en dus gedeelde refs) wint welke <PdfCanvasViewer> toevallig
  // als laatste in de tree commit, en tekent alle canvas-logica zich af op
  // DIE ene DOM-tak — de andere (zichtbare!) tak blijft dan een leeg, wit
  // canvas. Vandaar: gescheiden state/refs per tab, elk met hun eigen url.
  // Onthoudt de laatst gekozen PDF-weergave (Width/Page fit, óf een losse
  // zoom die geen van beide is) zodat die blijft staan tussen Next/Previous
  // case, én gedeeld wordt tussen het order-form-PDF en een pdf binnen Files
  // (beide hieronder gebruiken 'm als defaultFit/initialScale) - een
  // gebruiker die op "Width" heeft gezet, of met +/-/scroll/dubbelklik een
  // eigen zoomniveau heeft gekozen, wil dat niet steeds terug op "Page" zien
  // springen bij elke nieuwe case. CaseDetailDrawer blijft altijd gemount
  // (alleen de "open" prop toggled, zie TransparentDataGrid), dus deze state
  // overleeft vanzelf ook het sluiten en openen van een andere case via het
  // datagrid - alleen bij het allereerste gebruik (nog nooit iets gekozen)
  // is de fallback "page".
  const [pdfViewPrefs, setPdfViewPrefs] = useState({ fitMode: "page", scale: null });

  // Case-id waarvoor de scans klaar zijn (gemeld door Dracoloader): vanaf dan
  // mag het pdf van die case alvast geladen worden, ook op de Scans-tab
  const [scansSettledFor, setScansSettledFor] = useState(null);
  // CASE_PREFETCH: eigen "klaar"-melding die bij elke case-wissel weer leeg
  // gaat — scansSettledFor kan bij snel terugbladeren nog op deze case staan
  // van een eerdere keer, terwijl de scans nu opnieuw aan het laden zijn
  const [prefetchSettledFor, setPrefetchSettledFor] = useState(null);
  const handleScansSettled = useCallback((caseId) => {
    setScansSettledFor(caseId);
    if (CASE_PREFETCH) setPrefetchSettledFor(caseId);
  }, []);
  useEffect(() => {
    if (CASE_PREFETCH) setPrefetchSettledFor(null);
  }, [rowData?.id]);
  const prefetchOrderForm = PREFETCH_ORDER_FORM_PDF && rowData?.id != null && scansSettledFor === rowData.id;
  const orderFormPdfViewer = usePdfCanvasViewer({
    url: isOrderFormActive || prefetchOrderForm ? buildPdfProxyUrl(rowData?.orderform) : null,
    active: isOrderFormActive,
    cacheKey: `orderform-${rowData?.id}`,
    defaultFit: pdfViewPrefs.fitMode,
    initialScale: pdfViewPrefs.scale,
  });
  const miscPdfViewer = usePdfCanvasViewer({
    url: isMiscOpen && shownMiscFile?.isPdf ? buildPdfProxyUrl(shownMiscFile.fileLink) : null,
    // rowData?.id ERBIJ: de bestands-id's waren vroeger puur `misc-${index}`
    // (nu bevatten ze ook de case-id, dubbel zeker) - dus NIET uniek over cases heen. Zonder de
    // case-id hier ook in het cacheKey te verwerken, levert case A's eerste
    // misc-bestand en case B's eerste misc-bestand toevallig hetzelfde
    // cacheKey ("misc-misc-0") op, waardoor usePdfCanvasViewer dacht "dit heb
    // ik al geladen" en het PDF van de VORIGE case bleef tonen na het
    // wisselen van case - een echte verkeerde-order-PDF-bug.
    cacheKey: shownMiscFile ? `misc-${rowData?.id}-${shownMiscFile.id}` : null,
    defaultFit: pdfViewPrefs.fitMode,
    initialScale: pdfViewPrefs.scale,
    // ↑/↓ bladeren hier door de bijlagen (zie hieronder), niet door de pdf
    arrowKeys: false,
  });

  // Tab / Shift+Tab: door Scans → IOS Form → Attachments (alleen de aanwezige)
  const hasOrderForm = FEATURE_MISC_FILES_ENABLED() && !!rowData?.orderform;
  const hasMiscFiles = FEATURE_MISC_FILES_ENABLED() && miscFiles.length > 0;

  // CASE_PREFETCH: scans + IOS Form van de vorige/volgende case alvast in de
  // cache (zie utils/casePrefetch). Pas zodra de huidige case helemaal binnen
  // is (scans én, als dat geladen wordt, het pdf); bij een case-wissel stopt
  // het meteen en wordt alles buiten {vorige, huidige, volgende} opgeruimd.
  const prefetchPlan = useMemo(() => {
    if (!CASE_PREFETCH || !rowData) return null;
    const planFor = (row) => ({
      caseId: row.id,
      scans: initialScanLinks(row),
      pdfUrl: FEATURE_MISC_FILES_ENABLED() && row.orderform ? buildPdfProxyUrl(row.orderform) : null,
    });
    return { current: planFor(rowData), neighbors: (neighborRows || []).map(planFor) };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rowData, neighborRows]);
  const prefetchPlanKey = prefetchPlan ? JSON.stringify(prefetchPlan) : null;
  const pdfExpected = !!rowData?.orderform && (isOrderFormActive || prefetchOrderForm);
  const prefetchStart = !!open && rowData?.id != null && prefetchSettledFor === rowData.id
    && (!pdfExpected || orderFormPdfViewer.loadSettled);
  useEffect(() => {
    if (!CASE_PREFETCH) return;
    if (!open || !prefetchPlanKey) { clearCasePrefetch(); return; }
    updateCasePrefetch({ ...JSON.parse(prefetchPlanKey), start: prefetchStart });
  }, [open, prefetchPlanKey, prefetchStart]);
  useEffect(() => () => { if (CASE_PREFETCH) clearCasePrefetch(); }, []);
  useEffect(() => {
    if (!open) return;
    const cats = ["scans", hasOrderForm && "order-form", hasMiscFiles && "misc-files"].filter(Boolean);
    if (cats.length < 2) return;
    const onKeyDown = (e) => {
      if (e.key !== "Tab" || e.metaKey || e.ctrlKey || e.altKey) return;
      const el = document.activeElement;
      if (el?.tagName === "INPUT" || el?.tagName === "TEXTAREA" || el?.isContentEditable) return;
      e.preventDefault();
      setFileCategory((cur) => {
        const i = Math.max(cats.indexOf(cur), 0);
        return cats[(i + (e.shiftKey ? -1 : 1) + cats.length) % cats.length];
      });
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [open, hasOrderForm, hasMiscFiles]);

  // ↑/↓ bladeren door de bijlagen zolang Attachments open is
  useEffect(() => {
    if (!open || !isMiscOpen || miscFiles.length < 2) return;
    const onKeyDown = (e) => {
      if (e.key !== "ArrowDown" && e.key !== "ArrowUp") return;
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      const el = document.activeElement;
      if (el?.tagName === "INPUT" || el?.tagName === "TEXTAREA" || el?.isContentEditable) return;
      e.preventDefault();
      goToMiscOffset(e.key === "ArrowDown" ? 1 : -1);
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [open, isMiscOpen, miscFiles.length, goToMiscOffset]);
  const activePdfViewer = isOrderFormActive ? orderFormPdfViewer : miscPdfViewer;
  // Op IOS Form eerst het pdf, dan pas de scans (Dracoloader heeft zelf nog
  // een time-out als vangnet)
  const holdScanLoading = ORDER_FORM_PDF_FIRST && isOrderFormActive && !!rowData?.orderform
    && !orderFormPdfViewer.contentReady && orderFormPdfViewer.status !== "error";

  const { phase: pdfFlipPhase, startPdfFlip, flip: orderFormFlip } = usePdfFlip({
    active: isOrderFormActive,
    hasPdf: !!rowData?.orderform,
    caseId: rowData?.id,
    viewer: orderFormPdfViewer,
  });
  const startPdfFlipRef = useRef(startPdfFlip);
  startPdfFlipRef.current = startPdfFlip;

  // "Bevroren" versie van de ui van de actieve viewer: de toolbar leest
  // hieruit i.p.v. rechtstreeks uit activePdfViewer.ui, zodat 'ie tijdens het
  // laden van een nieuw document gewoon de vorige (stabiele) waarden blijft
  // tonen i.p.v. héél even een tussentijds foute stand (zie usePdfCanvasViewer
  // - numPages wordt al vroeg bijgewerkt, scale/pageIndex/fitMode pas
  // wanneer de eerste fit ook echt is toegepast).
  const stablePdfUi = useFrozenUntilReady(activePdfViewer.ui, activePdfViewer.contentReady);

  // Onthoudt een losse zoom (geen Width/Page fit) zodra die tot rust is
  // gekomen - ongeacht HOE die tot stand kwam (toolbar +/-, ctrl+scroll,
  // dubbelklik): die interacties lopen allemaal via usePdfCanvasViewer's
  // eigen event-handlers, niet via een aparte callback hierheen, dus dit is
  // de enige plek waar we zo'n wijziging betrouwbaar kunnen opvangen. Width/
  // Page zelf worden al expliciet bijgehouden via de knop-handlers verderop.
  useEffect(() => {
    // Zonder deze guard zou dit effect op het allereerste render (nog vóórdat
    // er ooit een document klaar is) al afgaan op de initiële placeholder-ui
    // (fitMode: null van useState's beginwaarde) en de default "page" direct
    // overschrijven met een niet-bestaande "losse zoom".
    if (!activePdfViewer.contentReady) return;
    // Ook een Width/Page fit die NIET via de toolbar-knoppen kwam (dubbelklik
    // = Fit page) onthouden; anders bleef de losse zoom van vóór de
    // dubbelklik staan en kwam die bij de volgende case terug.
    if (stablePdfUi.fitMode === "page" || stablePdfUi.fitMode === "width") {
      const mode = stablePdfUi.fitMode;
      setPdfViewPrefs((prev) => (prev.fitMode === mode ? prev : { fitMode: mode, scale: null }));
      return;
    }
    if (stablePdfUi.fitMode !== null) return;
    setPdfViewPrefs((prev) => (
      prev.fitMode === null && prev.scale === stablePdfUi.scale
        ? prev
        : { fitMode: null, scale: stablePdfUi.scale }
    ));
  }, [stablePdfUi, activePdfViewer.contentReady]);

  // ... rest of your component code with conditional returns below

  useEffect(() => {
    if (rowData?.id && isTransitioning) {
      // REMOVED: if (!expanded) { setIsTransitioning(false); }
      // For expanded: let loadHighlightPreviews handle it (unchanged)
      // For non-expanded: the nav timeout now controls the release (symmetric hold)
    }
  }, [rowData?.id, isTransitioning, expanded]); // Unchanged deps

  // NEW: Cleanup for navTimeout on unmount/close (add to your existing useEffects or a dedicated one)
  useEffect(() => {
    return () => {
      if (navTimeoutRef.current) clearTimeout(navTimeoutRef.current);
    };
  }, [open]);

  useEffect(() => {
    console.log("highlightPreviews state:", highlightPreviews);
    console.log("Object.entries:", Object.entries(highlightPreviews));
  }, [highlightPreviews]);

  // Keep ref up-to-date with latest nav state so the keyboard handler never goes stale
  keyNavRef.current = { index, allRowsLength, isLoading, onNewerRow, onOlderRow, highlightPreviews, localStatus, rowData, onUpdateRow, setLocalStatus };




  useEffect(() => {
    if (!open) return;
    const navDelay = 200;
    const handleKeyDown = (e) => {
      if (e.key !== "ArrowLeft" && e.key !== "ArrowRight" && e.key !== "Enter") return;
      const { index, allRowsLength, isLoading, onNewerRow, onOlderRow, highlightPreviews, localStatus, rowData, onUpdateRow, setLocalStatus } = keyNavRef.current;

      if (e.key === "Enter" && rowData && localStatus !== 'accepted' && FEATURE_ACCEPT_ENABLED()) {
        setLocalStatus('accepted');
        apiClient.patch(`/cases/${rowData.id}/status`, { status: 'accepted' })
          .then(() => {
            onUpdateRow?.(rowData.id, { status: 'accepted', flag: 1 });
            if (rowData.configuredPortalId) {
              apiClient.post('/processing_queue', { configured_portal_id: rowData.configuredPortalId, external_id: rowData.externalId, action: 'status', value: 'accepted' }).catch(console.error);
            }
          })
          .catch(() => setLocalStatus(localStatus));
        return;
      }
      const cleanup = () => {
        setHighlightPreviews({});
      };
      if (e.key === "ArrowLeft" && onNewerRow && index > 0) {
        startPdfFlipRef.current(-1);
        setFlashPrev(true);
        setTimeout(() => setFlashPrev(false), 120);
        if (navTimeoutRef.current) clearTimeout(navTimeoutRef.current);
        setIsTransitioning(true);
        navTimeoutRef.current = setTimeout(() => {
          cleanup();
          onNewerRow(index);
          setTimeout(() => setIsTransitioning(false), navDelay + 50);
        }, navDelay);
      } else if (e.key === "ArrowRight" && onOlderRow && index < allRowsLength - 1 && !isLoading) {
        startPdfFlipRef.current(1);
        setFlashNext(true);
        setTimeout(() => setFlashNext(false), 120);
        if (navTimeoutRef.current) clearTimeout(navTimeoutRef.current);
        setIsTransitioning(true);
        navTimeoutRef.current = setTimeout(() => {
          cleanup();
          onOlderRow(index);
          setTimeout(() => setIsTransitioning(false), navDelay + 50);
        }, navDelay);
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [open]);
  const CARD_CONFIG = {
    small: {
      width: 320,
      height: 348,
      bottom: 24,
      right: 40,
    },
    expanded: {
      width: 640,
      height: "calc(100% - 16px)",
      top: 8,
      bottom: 8,
      right: 8,
    },
  };
  // Refs for scroll containers
  const worktypesRef = useRef(null);
  const notesRef = useRef(null);
  // States for scroll detection
  const [worktypesAtBottom, setWorktypesAtBottom] = useState(true);
  const [worktypesScrollable, setWorktypesScrollable] = useState(false);
  const [notesAtBottom, setNotesAtBottom] = useState(true);
  const [notesScrollable, setNotesScrollable] = useState(false);

  // Worktypes scroll listener
  useEffect(() => {
    const ref = worktypesRef.current;
    if (!ref) return;
    const checkScrollability = () => {
      const { scrollHeight, clientHeight } = ref;
      const isScrollable = scrollHeight > clientHeight;
      setWorktypesScrollable(isScrollable);
      if (isScrollable) {
        // If scrollable, check initial position
        const { scrollTop } = ref;
        setWorktypesAtBottom(scrollTop + clientHeight >= scrollHeight - 1);
      } else {
        // If not scrollable, consider it "at bottom"
        setWorktypesAtBottom(true);
      }
    };
    const handleScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = ref;
      const atBottom = scrollTop + clientHeight >= scrollHeight - 1;
      setWorktypesAtBottom(atBottom);
    };
    // Initial check with multiple attempts to ensure DOM is settled
    checkScrollability();
    const timeouts = [
      setTimeout(checkScrollability, 0),
      setTimeout(checkScrollability, 50),
      setTimeout(checkScrollability, 150),
    ];
    ref.addEventListener("scroll", handleScroll);
    return () => {
      ref.removeEventListener("scroll", handleScroll);
      timeouts.forEach((t) => clearTimeout(t));
    };
  }, [bridges, singles, jawBased, wtAllocated]);

  // Notes scroll listener
  useEffect(() => {
    const ref = notesRef.current;
    if (!ref || !rowData?.remark) return;
    const checkScrollability = () => {
      const { scrollHeight, clientHeight, scrollTop } = ref;
      const isScrollable = scrollHeight > clientHeight;
      setNotesScrollable(isScrollable);
      if (isScrollable) {
        setNotesAtBottom(scrollTop + clientHeight >= scrollHeight - 1);
      } else {
        setNotesAtBottom(true);
      }
    };
    const handleScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = ref;
      setNotesAtBottom(scrollTop + clientHeight >= scrollHeight - 1);
    };
    checkScrollability();
    const timeouts = [
      setTimeout(checkScrollability, 0),
      setTimeout(checkScrollability, 50),
      setTimeout(checkScrollability, 150),
      setTimeout(checkScrollability, 300),
    ];
    ref.addEventListener("scroll", handleScroll);
    return () => {
      ref.removeEventListener("scroll", handleScroll);
      timeouts.forEach((t) => clearTimeout(t));
    };
  }, [rowData?.remark, expanded, wtAllocated, notesAllocated]);

  const measureUpdateRef = useRef(null);
  // Cache last measured value so small→large restores it without a flash
  const wtCacheRef = useRef(null);
  const wtCacheRowIdRef = useRef(null); // which row the cache belongs to
  const windowSizeAtMeasureRef = useRef({ w: 0, h: 0 });
  const prevExpandedRef = useRef(false); // was de kaart bij de vorige run al uitgeklapt?
  const measureFreshRowRef = useRef(false); // nieuwe case: altijd meten, cache overslaan

  // When the worktypes box mounts (can happen late, after highlights load for
  // single-tooth cases), fire update() in case the main effect already ran with
  // worktypesRef.current === null and therefore cleared the allocation.
  const worktypesRefCallback = useCallback((node) => {
    worktypesRef.current = node;
    if (node) setTimeout(() => measureUpdateRef.current?.(), 50);
  }, []);

  useEffect(() => {
    if (!expanded || !cardRef.current) {
      prevExpandedRef.current = false;
      setMeasureOverlay(null);
      setWtAllocated(null);
      setNotesAllocated(null);
      measureUpdateRef.current = null;
      return;
    }

    const update = () => {
      const cardRect = cardRef.current?.getBoundingClientRect();
      if (!cardRect) return;

      const itemsRect = worktypesRef.current?.getBoundingClientRect() ?? null;
      const cardBottomMargin = CARD_CONFIG.expanded.bottom;
      const worktypesNeeded = worktypesRef.current?.scrollHeight ?? 0;

      let notesNeeded = 0, notesHeaderHeight = 0, notesContentNeeded = 0;
      if (notesRef.current && notesHeaderRef.current) {
        notesHeaderHeight = notesHeaderRef.current.offsetHeight + 24;
        const el = notesRef.current;
        const prevFlex = el.style.flex;
        const prevHeight = el.style.height;
        el.style.flex = "none";
        el.style.height = "auto";
        el.getBoundingClientRect();
        notesContentNeeded = el.scrollHeight;
        el.style.flex = prevFlex;
        el.style.height = prevHeight;
        notesNeeded = notesHeaderHeight + notesContentNeeded;
      }

      if (!itemsRect) {
        // Geen worktypes: de notes krijgen alle ruimte (flex: 1). Ook hun
        // toewijzing wissen, anders bleef de hoogte van de vorige case staan
        // en werd een korte notitie afgeknipt (met fade)
        setMeasureOverlay(null);
        setWtAllocated(null);
        setNotesAllocated(null);
        return;
      }

      const availableHeight = (cardRect.bottom - cardBottomMargin) - itemsRect.top;
      if (availableHeight <= 0) return;

      const gap = notesNeeded > 0 ? 16 : 0;
      const effectiveAvailable = availableHeight - gap;

      let wtHeight, notesHeight;
      if (notesNeeded === 0) {
        wtHeight = Math.min(worktypesNeeded, availableHeight);
        notesHeight = 0;
      } else if (worktypesNeeded + notesNeeded <= effectiveAvailable) {
        // Both fit completely — no scrolling needed
        wtHeight = worktypesNeeded;
        notesHeight = notesNeeded;
      } else {
        // Past niet allebei helemaal: verdeel de ruimte (zie hieronder)
        const notesMin = notesHeaderHeight + 48;
        // Minimaal de eerste twee worktypes helemaal (gemeten, incl. de gap
        // ertussen); een vaste 96 (2 × 48) knipte er een paar px af, waardoor
        // 2 worktypes al scrollbaar werden en de fade kregen
        // Rijen tellen, geen groepen: een brug is één child met een kop en
        // meerdere rijen, dus children[1] kon twee hele bruggen zijn en liet
        // dan geen ruimte meer over voor de notes
        const wtEl = worktypesRef.current;
        const wtRows = wtEl?.querySelectorAll("[data-wt-row]") ?? [];
        const secondItem = wtRows[Math.min(1, wtRows.length - 1)];
        const wtMin = secondItem
          ? Math.min(worktypesNeeded, Math.ceil(secondItem.getBoundingClientRect().bottom - itemsRect.top + wtEl.scrollTop))
          : 96;
        // De notes krijgen tot NOTES_SHARE van de ruimte (of meer als de
        // worktypes minder nodig hebben); de worktypes de rest. Eerder werd
        // het tekort naar verhouding verdeeld, waardoor een lange worktype-
        // lijst de notes tot een paar regels samenperste.
        const notesShare = Math.round(effectiveAvailable * NOTES_SHARE);
        notesHeight = Math.min(notesNeeded, Math.max(notesShare, effectiveAvailable - worktypesNeeded));
        notesHeight = Math.max(notesHeight, notesMin);
        wtHeight = effectiveAvailable - notesHeight;
        if (wtHeight < wtMin) {
          wtHeight = wtMin;
          notesHeight = Math.max(notesMin, effectiveAvailable - wtMin);
        }
        // Zo weinig ruimte dat ook de minimums niet passen: dan houden de
        // notes hun minimum en scrollen de worktypes (anders viel de note
        // helemaal weg)
        if (wtHeight + notesHeight > effectiveAvailable) {
          notesHeight = notesMin;
          wtHeight = Math.max(48, effectiveAvailable - notesMin);
        }
      }
      wtHeight = Math.round(wtHeight);
      notesHeight = Math.round(notesHeight);
      setWtAllocated(wtHeight);
      setNotesAllocated(notesHeight > 0 ? notesHeight : null);
      wtCacheRef.current = wtHeight;
      wtCacheRowIdRef.current = rowData?.id;
      windowSizeAtMeasureRef.current = { w: window.innerWidth, h: window.innerHeight };

      setMeasureOverlay({
        top: itemsRect.top - cardRect.top,
        height: availableHeight,
        worktypesNeeded,
        notesHeaderHeight,
        notesContentNeeded,
        notesNeeded,
      });
    };

    measureUpdateRef.current = update;

    // Use cache only for the same row (expand/contract toggle) with unchanged window.
    // For a fresh row open from DataGrid the cache belongs to a different row — run
    // update() immediately so there's no delayed bump.
    // Na een case-wissel (of verse open) komen de highlightPreviews later binnen en
    // verandert de worktype-lijst (singles komen uit de previews). Die run moet dan
    // direct meten i.p.v. de cache terug te zetten, anders klopt de verdeling pas
    // bij de retry van 300/600ms. Bij in-/uitklappen van dezelfde case blijft de cache.
    const justExpanded = !prevExpandedRef.current;
    prevExpandedRef.current = true;
    const isSameRow = rowData?.id === wtCacheRowIdRef.current;
    const windowUnchanged =
      window.innerWidth === windowSizeAtMeasureRef.current.w &&
      window.innerHeight === windowSizeAtMeasureRef.current.h;
    const useCache =
      isSameRow && windowUnchanged && wtCacheRef.current != null &&
      (justExpanded || !measureFreshRowRef.current);
    if (justExpanded) measureFreshRowRef.current = !useCache;
    else if (!isSameRow) measureFreshRowRef.current = true;
    if (useCache) {
      setWtAllocated(wtCacheRef.current);
    } else {
      update();
    }

    // Retries at 300ms and 600ms catch the Drawer/card animation settling
    // and any async highlight loading that finishes after the first call.
    const t1 = setTimeout(update, 300);
    const t2 = setTimeout(update, 600);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, [expanded, rowData?.id, highlightPreviews]);

  useEffect(() => {
    const onResize = () => measureUpdateRef.current?.();
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  if (!rowData) return null;
  const cardConfig = expanded ? CARD_CONFIG.expanded : CARD_CONFIG.small;
  const isPending = pendingNewRows.some((r) => r.id === rowData.id);
  const contentOpacity = isTransitioning || isContentLoading ? 0 : 1;

  const handleNewerRow = (e) => {
    e.stopPropagation();
    if (onNewerRow && index > 0) {
      startPdfFlip(-1);
      if (navTimeoutRef.current) clearTimeout(navTimeoutRef.current);
      setIsTransitioning(true);
      const navDelay = parseFloat(fadeDuration) * 1000;
      navTimeoutRef.current = setTimeout(() => {
        // Opruimen VÓÓR navigation
        setHighlightPreviews({});

        onNewerRow(index);
        setTimeout(() => {
          setIsTransitioning(false);
        }, navDelay + 50);
      }, navDelay);
    }
  };

  const handleOlderRow = (e) => {
    e.stopPropagation();
    console.log(
      "Next button clicked! Index:",
      index,
      "onOlderRow:",
      !!onOlderRow,
    );
    if (onOlderRow && index < allRowsLength - 1 && !isLoading) {
      startPdfFlip(1);
      if (navTimeoutRef.current) clearTimeout(navTimeoutRef.current);
      setIsTransitioning(true);
      const navDelay = parseFloat(fadeDuration) * 1000;
      navTimeoutRef.current = setTimeout(() => {
        setHighlightPreviews({});
        onOlderRow(index);
        setTimeout(() => {
          setIsTransitioning(false);
        }, navDelay + 50);
      }, navDelay);
    } else if (isLoading) {
      pendingNextRef.current = true;
      setAwaitingNext(true);
    } else {
      console.log(
        "Next blocked: index=",
        index,
        "onOlderRow exists?",
        !!onOlderRow,
        "isLoading:",
        isLoading,
      );
    }
  };

  const isNewerRowDisabled = index === 0;
  const isOlderRowDisabled = index === allRowsLength - 1 && !isLoading;
  // Stippen op de achtergrond (8px tegel, 1px stip). Bij een gebroken
  // pixelverhouding (bijv. 1,6×) op hele schermpixels, anders valt elke stip
  // anders (ruitpatroon). Bij 1×/2×/3× blijft het 8 en 1.
  const dpr = window.devicePixelRatio || 1;
  const dotGrid = Math.abs(dpr - Math.round(dpr)) > 0.01
    ? { tile: Math.round(8 * dpr) / dpr, dot: Math.max(1, Math.round(dpr)) / dpr }
    : { tile: 8, dot: 1 };
  let imageSet;
  const iconType = getHighestIconType(rowData.worktypes);
  imageSet = getBackgroundImageSetForIconType(iconType);
  const bgImageSetList = [`url(${imageSet.src}) 1x`, ...imageSet.srcSet.split(",").map((part) => {
    const [url, density] = part.trim().split(" ");
    return `url(${url}) ${density}`;
  })].join(", ");
  if (isCompact) {
    // Zelfde accepteer-actie als de knop op de kaart hieronder
    const acceptCase = async () => {
      if (!rowData || localStatus === "accepted") return;
      const newStatus = "accepted";
      setLocalStatus(newStatus);
      try {
        await apiClient.patch(`/cases/${rowData.id}/status`, { status: newStatus });
        onUpdateRow?.(rowData.id, { status: newStatus, flag: 1 });
        if (rowData.configuredPortalId) {
          apiClient.post('/processing_queue', { configured_portal_id: rowData.configuredPortalId, external_id: rowData.externalId, action: 'status', value: newStatus }).catch(console.error);
        }
      } catch (err) {
        console.error("Failed to update status:", err);
        setLocalStatus(localStatus);
      }
    };
    const noEvent = { stopPropagation() {} };
    return (
      <CaseDetailTouch
        ctx={{
          open, onClose, rowData, index, allRowsLength,
          goNewer: () => handleNewerRow(noEvent),
          goOlder: () => handleOlderRow(noEvent),
          isNewerRowDisabled, isOlderRowDisabled, isLoading, awaitingNext,
          contentOpacity, isTransitioning,
          localStatus, acceptCase, isDownloading, onDownloadPdf, onDownloadZipWithPdf, onDownloadStlZip,
          chartRef, dentalHighlights: generateDentalHighlights(rowData.worktypes), highlightPreviews,
          jawBased, bridges, singles, bgImage: imageSet,
          fileCategory, setFileCategory, hasOrderForm, hasMiscFiles,
          viewerData, noScans, scanMode, onScanModeChange: handleScanModeChange,
          holdScanLoading, onScansSettled: handleScansSettled,
          orderFormPdfViewer, miscPdfViewer, activePdfViewer, stablePdfUi, setPdfViewPrefs, orderFormMissing,
          shownMiscFile, shownMiscFiles, selectedMiscFile, selectMiscFile, goToMiscOffset, handleMiscSwipe,
          miscFileIndex, miscUrls, loadedMiscIds, miscErrorIds, markMiscLoaded, markMiscError, miscNavDirection, allThumbsReady,
        }}
      />
    );
  }

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      variant="temporary"
      ModalProps={{ keepMounted: false }}
      slotProps={{
        paper: {
          sx: {
            position: "fixed",
            top: 8,
            bottom: 8,
            right: 8,
            height: "calc(100% - 16px)",
            width:
              typeof window !== "undefined" ? window.innerWidth - 16 : 1080,
            backgroundColor: "rgba(50, 50, 50, 0.1)",
            color: "#fff",
            backdropFilter: "blur(12px)",
            WebkitBackdropFilter: "blur(12px)",
            border: "1px solid rgba(255,255,255,0.1)",
            borderRadius: "8px",
            overflow: "hidden",
            outline: "none",
          },
        },
      }}
    >
      <Box sx={{ position: "absolute", inset: 0, zIndex: 0 }}>
        <svg width="100%" height="100%" style={{ position: "absolute" }}>
          <defs>
            <pattern
              id="dots"
              width={dotGrid.tile}
              height={dotGrid.tile}
              patternUnits="userSpaceOnUse"
            >
              <rect width={dotGrid.dot} height={dotGrid.dot} fill="rgba(255,255,255,0.1)" />
            </pattern>
            <mask id="cardMask">
              <rect width="100%" height="100%" fill="white" />
              <rect
                x={`calc(100% - ${expanded
                    ? CARD_CONFIG.expanded.width + CARD_CONFIG.expanded.right
                    : CARD_CONFIG.small.width + CARD_CONFIG.small.right
                  }px)`}
                y={
                  expanded
                    ? `${CARD_CONFIG.expanded.top}px`
                    : `calc(100% - ${CARD_CONFIG.small.height}px - ${CARD_CONFIG.small.bottom}px)`
                }
                width={
                  expanded
                    ? CARD_CONFIG.expanded.width
                    : CARD_CONFIG.small.width
                }
                height={
                  expanded
                    ? CARD_CONFIG.expanded.height
                    : CARD_CONFIG.small.height
                }
                rx="8"
                fill="black"
                style={{
                  transition:
                    "x 0.4s ease, y 0.4s ease, width 0.4s ease, height 0.4s ease",
                }}
              />
            </mask>
          </defs>
          <rect
            width="100%"
            height="100%"
            fill="url(#dots)"
            mask="url(#cardMask)"
          />
        </svg>
      </Box>
      <Box
        sx={{
          position: "absolute",
          top: "16px",
          right: "32px",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: "8px",
          zIndex: 15,
        }}
      >
        <Box sx={{ display: "flex", gap: "8px" }}>
        <Box
          sx={{
            display: "flex",
            width: "48px",
            height: "48px",
            justifyContent: "center",
            alignItems: "center",
            flexShrink: 0,
            borderRadius: "50%",
            border: flashPrev ? "1px solid rgba(255, 255, 255, 0.6)" : "1px solid rgba(255, 255, 255, 0.10)",
            background: flashPrev ? "rgba(255, 255, 255, 0.2)" : "rgba(255, 255, 255, 0.10)",
            backdropFilter: "blur(4px)",
            cursor: isNewerRowDisabled ? "not-allowed" : "pointer",
            transition: flashPrev ? "none" : "border 0.25s ease, background 0.25s ease",
            opacity: isNewerRowDisabled ? 0.5 : 1,
            pointerEvents: isNewerRowDisabled ? "none" : "auto",
            "&:hover": isNewerRowDisabled
              ? {}
              : { background: "rgba(255,255,255,0.2)" },
          }}
          onClick={handleNewerRow}
        >
          <img
            src={PreviousIcon}
            alt="Previous"
            draggable={false}
            onContextMenu={(e) => e.preventDefault()}
            onDragStart={(e) => e.preventDefault()}
            style={{
              userSelect: "none",
              width: 16,
              height: 16,
              objectFit: "contain",
            }}
          />
        </Box>
        <Box
          sx={{
            display: "flex",
            width: "48px",
            height: "48px",
            justifyContent: "center",
            alignItems: "center",
            flexShrink: 0,
            borderRadius: "50%",
            border: flashNext ? "1px solid rgba(255, 255, 255, 0.6)" : "1px solid rgba(255, 255, 255, 0.10)",
            background: flashNext ? "rgba(255, 255, 255, 0.2)" : "rgba(255, 255, 255, 0.10)",
            backdropFilter: "blur(4px)",
            cursor: isOlderRowDisabled ? "not-allowed" : "pointer",
            transition: flashNext ? "none" : "border 0.25s ease, background 0.25s ease",
            opacity: isOlderRowDisabled ? 0.5 : 1,
            pointerEvents: isOlderRowDisabled ? "none" : "auto",
            "&:hover": isOlderRowDisabled
              ? {}
              : { background: "rgba(255,255,255,0.2)" },
          }}
          onClick={handleOlderRow}
        >
          {isLoading && awaitingNext ? (
            <CircularProgress
              size={16}
              thickness={3}
              sx={{ color: "rgba(255,255,255,0.8)" }}
            />
          ) : (
            <img
              src={NextIcon}
              alt="Next"
              draggable={false}
              onContextMenu={(e) => e.preventDefault()}
              onDragStart={(e) => e.preventDefault()}
              style={{
                userSelect: "none",
                width: 16,
                height: 16,
                objectFit: "contain",
              }}
            />
          )}
        </Box>
        </Box>
      </Box>

      {/* Close Button */}
      <Box
        sx={{
          position: "absolute",
          top: "16px",
          left: "32px",
          zIndex: 15,
        }}
      >
        <Box
          sx={{
            display: "flex",
            width: "48px",
            height: "48px",
            justifyContent: "center",
            alignItems: "center",
            flexShrink: 0,
            borderRadius: "50%",
            border: "1px solid rgba(255, 255, 255, 0.10)",
            background: "rgba(255, 255, 255, 0.10)",
            backdropFilter: "blur(4px)",
            cursor: "pointer",
            transition: "all 0.3s ease",
            "&:hover": { background: "rgba(255,255,255,0.2)" },
          }}
          onClick={(e) => {
            e.stopPropagation();
            onClose();
          }}
        >
          <CloseIcon />
        </Box>
      </Box>

      <Box
        sx={{ position: "relative", zIndex: 1, width: "100%", height: "100%" }}
      >
        {/* Altijd zichtbaar, in alle drie de categorieën — dit is nu de enige
            manier om tussen scans/pdf/files te wisselen. */}
        <Box
          sx={{
            position: "absolute",
            top: "16px",
            left: 0,
            right: `${cardConfig.right + cardConfig.width}px`,
            zIndex: 20,
            display: "flex",
            justifyContent: "center",
            transition: "right 0.4s ease",
            pointerEvents: "none",
            "& > *": { pointerEvents: "auto" },
          }}
        >
          <FileCategorySelector
            key={selectorKey}
            category={fileCategory}
            onCategoryChange={setFileCategory}
            showOrderForm={hasOrderForm}
            showMiscFiles={hasMiscFiles}
            scanMode={scanMode}
            portalLogo={portals[rowData.scannerBrand]?.logo}
          />
        </Box>

        {/* Bestands- en pdf-controls voor de PDF- en Files-tab, op dezelfde
            plek als Front/Top/Side bij Scans (zie Dracoloader). */}
        <Box
          sx={{
            position: "absolute",
            bottom: `${FILE_CONTROLS_BOTTOM}px`,
            left: 0,
            right: `${cardConfig.right + cardConfig.width}px`,
            zIndex: 20,
            display: "flex",
            justifyContent: "center",
            transition: "right 0.4s ease",
            pointerEvents: "none",
            "& > *": { pointerEvents: "auto" },
          }}
        >
          <FileViewerControls
            category={fileCategory}
            fileIndex={miscFileIndex}
            fileCount={shownMiscFiles.length}
            onFilePrev={() => goToMiscOffset(-1)}
            onFileNext={() => goToMiscOffset(1)}
            isCurrentFilePdf={!!shownMiscFile?.isPdf}
            pdfPageIndex={stablePdfUi.pageIndex}
            pdfNumPages={stablePdfUi.numPages}
            pdfZoom={stablePdfUi.scale}
            pdfFitMode={stablePdfUi.fitMode}
            onPdfPrevPage={() => activePdfViewer.actions.goToPage(activePdfViewer.ui.pageIndex - 1)}
            onPdfNextPage={() => activePdfViewer.actions.goToPage(activePdfViewer.ui.pageIndex + 1)}
            onPdfZoomIn={activePdfViewer.actions.zoomIn}
            onPdfZoomOut={activePdfViewer.actions.zoomOut}
            onPdfZoomReset={activePdfViewer.actions.resetZoom}
            onPdfFitWidth={() => { activePdfViewer.actions.fitWidth(); setPdfViewPrefs({ fitMode: "width", scale: null }); }}
            onPdfFitPage={() => { activePdfViewer.actions.fitPage(); setPdfViewPrefs({ fitMode: "page", scale: null }); }}
            // Opent direct de printdialoog met de pdf die nu in de viewer staat
            // (IOS Form of een pdf in Attachments)
            onPdfPrint={activePdfViewer.actions.print}
            pdfPrintDisabled={!activePdfViewer.contentReady}
          />
        </Box>

        {/* Printknop rechtsboven in het viewer-vlak (links van de kaart),
            alleen bij een pdf (IOS Form of een pdf in Attachments). */}
        {PDF_PRINT_TOP_RIGHT && (
          <Box
            sx={{
              position: "absolute",
              top: "16px",
              right: `${cardConfig.right + cardConfig.width + 32}px`,
              zIndex: 20,
              transition: "right 0.4s ease",
            }}
          >
            <PdfPrintButton
              // Bij next/previous case helemaal weg-faden (contentOpacity) en
              // pas terugkomen als de nieuwe pdf klaar is, i.p.v. gedimd blijven staan
              visible={
                (fileCategory === "order-form" || (fileCategory === "misc-files" && !!shownMiscFile?.isPdf)) &&
                contentOpacity === 1 && activePdfViewer.contentReady
              }
              disabled={!activePdfViewer.contentReady}
              onClick={activePdfViewer.actions.print}
            />
          </Box>
        )}

        <Box
          sx={{
            width: "100%",
            height: "100%",
            // De weg-/in-animatie van de scans zelf gebeurt nu native in
            // Dracoloader (dezelfde GSAP shrink/rotate/fade als bij het
            // wisselen van case, via de hidden-prop) — hier hoeft alleen de
            // interactie geblokkeerd te worden zolang scans niet actief zijn.
            pointerEvents: isScansActive ? "auto" : "none",
          }}
        >
          <React.Suspense
            fallback={
              <Box sx={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <CircularProgress sx={{ color: "rgba(255,255,255,0.8)" }} />
              </Box>
            }
          >
            <Draco
              viewerData={viewerData}
              expanded={expanded}
              noScans={noScans}
              // Ook "hidden" (voor Dracoloader's frameloop-pause) wanneer de
              // hele drawer dicht is — anders blijft de WebGL-canvas 60fps
              // door-renderen (incl. OrbitControls-damping) ook als er nog
              // nooit een case is geopend. Zie CLAUDE.md-achtige findings:
              // <Canvas> had geen frameloop-prop (dus impliciet "always"),
              // wat een continue GPU/native-memory-groei gaf onafhankelijk
              // van of er iets te zien viel.
              hidden={!isScansActive || !open}
              scanMode={scanMode}
              onScanModeChange={handleScanModeChange}
              holdLoading={holdScanLoading}
              caseKey={rowData?.id ?? null}
              worktypes={rowData?.worktypes ?? null}
              onLoadSettled={handleScansSettled}
            />
          </React.Suspense>
        </Box>

        {isMiscOpen && miscFiles.length === 0 && (
          <Box
            sx={{
              position: "absolute",
              top: 0,
              bottom: 0,
              left: 0,
              right: `${cardConfig.right + cardConfig.width}px`,
              zIndex: 2,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Typography sx={{ color: "rgba(255,255,255,0.6)" }}>
              Geen extra bestanden voor deze case
            </Typography>
          </Box>
        )}

        {shownMiscFile && (
          <>
            {/* Links: verticale thumbnail-kolom, verticaal gecentreerd, met
                een EIGEN fade los van de hoofdinhoud — anders vermenigvuldigt
                de gestaggerde per-thumb opacity zich met de opacity van een
                gemeenschappelijke ouder en valt de stagger in de praktijk niet
                op (beide waren binnen dezelfde ~0.4s klaar). */}
            <Box
              sx={{
                position: "absolute",
                top: 0,
                bottom: 0,
                left: "32px",
                zIndex: 2,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                gap: "16px",
                pointerEvents: isMiscOpen ? "auto" : "none",
              }}
            >
              {shownMiscFiles.length > 1 && React.createElement(
                THUMBS_MAX_VISIBLE != null ? ThumbScroller : Box,
                THUMBS_MAX_VISIBLE != null
                  ? {
                      count: shownMiscFiles.length,
                      activeIndex: shownMiscFiles.findIndex((f) => f.id === selectedMiscFile?.id),
                      ready: allThumbsReady,
                      visible: isMiscOpen,
                    }
                  : {
                      sx: {
                        display: "flex",
                        flexDirection: "column",
                        gap: "8px",
                        maxHeight: "60%",
                        overflowY: "auto",
                        scrollbarWidth: "none",
                        msOverflowStyle: "none",
                        "&::-webkit-scrollbar": { display: "none" },
                      },
                    },
                  !allThumbsReady ? (
                    // Zelfde totale hoogte als de uiteindelijke lijst (80px per
                    // thumb + 8px gap), anders verspringt "Back to scans"
                    // eronder zodra de spinner plaatsmaakt voor de volle lijst.
                    <Box
                      sx={{
                        width: "80px",
                        height: `${shownMiscFiles.length * 80 + (shownMiscFiles.length - 1) * 8}px`,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <CircularProgress size={24} sx={{ color: "rgba(255,255,255,0.6)" }} />
                    </Box>
                  ) : (
                    shownMiscFiles.map((f, index) => {
                      const isActive = selectedMiscFile?.id === f.id;
                      // Zelfde stagger-delay voor zowel het infaden (zodra
                      // alles klaar is) als het uitfaden (bij sluiten) — elke
                      // volgende thumb iets later dan de vorige.
                      const delay = index * 0.08;
                      return (
                        // Bewust een kale motion.div (geen MUI Box) — Box'
                        // onderliggende styled('div') filtert non-HTML props
                        // als initial/animate/transition er stilletjes uit
                        // zodra je 'm via component={motion.div} laat
                        // renderen, waardoor er niks animeerde.
                        // De motion.div doet alleen het gestaggerde in-/uitfaden
                        // bij openen/sluiten. De actieve/hover-stand zit op de
                        // binnenste Box met een gewone CSS-transitie: zat die
                        // ook in de motion-animatie, dan kreeg elke wissel de
                        // stagger-delay mee en sprong de rand (1px -> 2px)
                        // direct, terwijl de opacity pas later volgde. Dat gaf
                        // de flikkering na het wisselen.
                        <motion.div
                          key={f.id}
                          onClick={(e) => {
                            e.stopPropagation();
                            if (!isActive) selectMiscFile(f);
                          }}
                          initial={{ opacity: 0, scale: 0.6 }}
                          animate={{
                            opacity: isMiscOpen ? 1 : 0,
                            scale: isMiscOpen ? 1 : 0.6,
                          }}
                          transition={{ duration: 0.4, ease: [0.32, 0.72, 0, 1], delay }}
                          style={{ flexShrink: 0, width: "80px", height: "80px" }}
                        >
                          <Box
                            sx={{
                              position: "relative",
                              width: "100%",
                              height: "100%",
                              borderRadius: "8px",
                              overflow: "hidden",
                              cursor: isActive ? "default" : "pointer",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              background: "rgba(30,41,59,0.6)",
                              opacity: isActive ? 1 : 0.6,
                              transition: "opacity 0.2s ease",
                              "&:hover": isActive ? {} : { opacity: 0.9 },
                              // Rand als overlay, zodat de dikte kan wisselen
                              // zonder dat het plaatje verschuift.
                              "&::after": {
                                content: '""',
                                position: "absolute",
                                inset: 0,
                                borderRadius: "8px",
                                pointerEvents: "none",
                                boxShadow: isActive
                                  ? "inset 0 0 0 2px rgba(255,255,255,0.9)"
                                  : "inset 0 0 0 1px rgba(255,255,255,0.2)",
                                transition: "box-shadow 0.2s ease",
                              },
                            }}
                          >
                            {!f.isImage ? (
                              <>
                                <svg
                                  width="12"
                                  height="13"
                                  viewBox="0 0 12 13"
                                  fill="none"
                                  style={{ position: "absolute", top: 12, left: 12 }}
                                >
                                  <path
                                    d="M7.96653 3.80474L4.19526 7.57596C3.93491 7.83629 3.93491 8.25842 4.19526 8.51876C4.45561 8.77916 4.87773 8.77916 5.13806 8.51876L8.90933 4.74754C9.69033 3.9665 9.69033 2.70017 8.90933 1.91912C8.12826 1.13807 6.86193 1.13807 6.08086 1.91912L2.30964 5.69036C1.00789 6.99209 1.00789 9.10262 2.30964 10.4044C3.61139 11.7062 5.72193 11.7062 7.02366 10.4044L10.7949 6.63316L11.7377 7.57596L7.96653 11.3472C6.14406 13.1696 3.18928 13.1696 1.36683 11.3472C-0.455612 9.52476 -0.455612 6.57002 1.36683 4.74754L5.13806 0.97631C6.4398 -0.325437 8.5504 -0.325437 9.85213 0.97631C11.1539 2.27806 11.1539 4.3886 9.85213 5.69036L6.08086 9.46162C5.29986 10.2426 4.0335 10.2426 3.25245 9.46162C2.4714 8.68056 2.4714 7.41422 3.25245 6.63316L7.02366 2.86193L7.96653 3.80474Z"
                                    fill="white"
                                  />
                                </svg>
                                <span
                                  style={{
                                    position: "absolute",
                                    left: 12,
                                    bottom: 12,
                                    color: "rgba(255, 255, 255, 0.60)",
                                    fontFamily: '"DM Mono", monospace',
                                    fontSize: "9px",
                                    fontStyle: "normal",
                                    fontWeight: 500,
                                    lineHeight: "9px",
                                    letterSpacing: "0.72px",
                                    textTransform: "uppercase",
                                  }}
                                >
                                  {f.ext || "file"}
                                </span>
                              </>
                            ) : (
                              <img
                                src={miscUrls[f.id]}
                                alt=""
                                draggable={false}
                                onLoad={() => markMiscLoaded(f.id)}
                                onError={() => markMiscLoaded(f.id)}
                                style={{ width: "100%", height: "100%", objectFit: "cover" }}
                              />
                            )}
                          </Box>
                        </motion.div>
                      );
                    })
                  )
              )}

            </Box>

            {/* Hoofdinhoud, verschoven naar rechts om ruimte te maken voor de
                thumbnail-sidebar links. Eigen fade/scale, los van de sidebar. */}
            <Box
              sx={{
                position: "absolute",
                // Zelfde verticale ruimte als de PDF-tab: vrij van de
                // categorie-pil bovenin en de controls onderin.
                top: "96px",
                bottom: `${FILE_VIEWER_BOTTOM}px`,
                left: "160px",
                // Blijf uit de buurt van de info-card rechts, of die nu wel of
                // niet expanded is — zelfde aanpak als Dracoloader's cardOffset.
                // Rechts dezelfde 160px extra als links (thumbnail-kolom), zodat
                // het bestand op dezelfde as staat als de toolbars.
                right: `${cardConfig.right + cardConfig.width + 160}px`,
                zIndex: 2,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                opacity: isMiscOpen ? 1 : 0,
                transform: isMiscOpen ? "scale(1)" : "scale(0.96)",
                transition: "opacity 0.4s ease, transform 0.4s ease, right 0.4s ease",
                pointerEvents: isMiscOpen ? "auto" : "none",
              }}
            >
              <Box
                sx={{
                  position: "absolute",
                  inset: 0,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  // PdfCanvasViewer toont zijn eigen spinner terwijl het
                  // document laadt, dus deze overlay slaat een pdf-bestand
                  // over — anders blijft 'ie hangen (loadedMiscIds wordt voor
                  // pdf's nooit gezet, er is geen <img> die 'm zou vullen).
                  opacity:
                    isMiscOpen &&
                    !miscErrorIds.has(shownMiscFile?.id) &&
                    !shownMiscFile?.isPdf &&
                    !loadedMiscIds.has(shownMiscFile?.id)
                      ? 1
                      : 0,
                  transition: "opacity 0.25s ease",
                  pointerEvents: "none",
                }}
              >
                <CircularProgress sx={{ color: "rgba(255,255,255,0.8)" }} />
              </Box>

              {miscErrorIds.has(shownMiscFile?.id) && (
                <Typography sx={{ color: "rgba(255,255,255,0.8)" }}>
                  Failed to load file
                </Typography>
              )}

              {!miscErrorIds.has(shownMiscFile?.id) && shownMiscFile && (
                <Box sx={{ position: "relative", width: "100%", height: "100%", overflow: "hidden" }}>
                  <AnimatePresence initial={false} custom={miscNavDirection}>
                    {/* Buitenste laag: ALLEEN de enter/exit-slide (100% per
                        keer), geen drag-props hier. De binnenste laag hieronder
                        regelt ALLEEN het live sleepgebaar. Toen beide op
                        hetzelfde element zaten, klemde dragConstraints={{left:0,
                        right:0}} de 100%-startpositie meteen terug naar ~0 —
                        vandaar dat de nieuwe plaat "al in beeld stond en dan nog
                        een stukje bewoog" in plaats van er echt in te schuiven. */}
                    <motion.div
                      key={shownMiscFile.id}
                      custom={miscNavDirection}
                      variants={{
                        enter: (dir) => ({ x: `${dir * 100}%`, zIndex: 1 }),
                        center: { x: 0, zIndex: 1 },
                        exit: (dir) => ({ x: `${-dir * 100}%`, zIndex: 0 }),
                      }}
                      initial="enter"
                      animate="center"
                      exit="exit"
                      transition={{ duration: 0.4, ease: [0.32, 0.72, 0, 1] }}
                      style={{ position: "absolute", inset: 0 }}
                    >
                      <MiscSwipeArea
                        enabled={shownMiscFiles.length > 1}
                        onSwipe={handleMiscSwipe}
                        style={{
                          width: "100%",
                          height: "100%",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          touchAction: "pan-y",
                          cursor: shownMiscFiles.length > 1 ? "grab" : "default",
                        }}
                      >
                        {/* De schuifbeweging van dit frame reageert meteen op
                            shownMiscFile — de content (img/iframe) rendert
                            meteen mee zodra de url er is, zonder te wachten
                            tot 'ie volledig gedecodeerd is. Anders schuift er
                            een lege frame naar binnen en verschijnt de content
                            er pas ná de slide bij (zag eruit als "staat er al,
                            scrollt dan nog een stukje" in plaats van er echt
                            in te schuiven). */}
                        {shownMiscFile.isPdf ? (
                          <Box
                            sx={{
                              position: "relative",
                              height: "100%",
                              width: "100%",
                              maxWidth: "950px",
                              borderRadius: "8px",
                            }}
                          >
                            <PdfCanvasViewer viewer={miscPdfViewer} suppressSpinner={contentOpacity === 0} />
                          </Box>
                        ) : (
                          miscUrls[shownMiscFile.id] && (
                            <ZoomableImage
                              src={miscUrls[shownMiscFile.id]}
                              alt={shownMiscFile.filename}
                              draggable={false}
                              onLoad={() => markMiscLoaded(shownMiscFile.id)}
                              onError={() => markMiscError(shownMiscFile.id)}
                              sx={{
                                maxWidth: "100%",
                                maxHeight: "100%",
                                objectFit: "contain",
                                borderRadius: "8px",
                                opacity: loadedMiscIds.has(shownMiscFile.id) ? 1 : 0,
                                transition: "opacity 0.25s ease",
                              }}
                            />
                          )
                        )}
                      </MiscSwipeArea>
                    </motion.div>
                  </AnimatePresence>
                </Box>
              )}
            </Box>
          </>
        )}

        {/* "PDF"-tab: het order-form, altijd gemount (net als Dracoloader)
            zodat wisselen weg/terug nette fades geeft i.p.v. een harde knip. */}
        <Box
          sx={{
            position: "absolute",
            top: "96px",
            bottom: `${FILE_VIEWER_BOTTOM}px`,
            // Links en rechts evenveel marge, zodat de pdf op dezelfde as
            // staat als de toolbars.
            left: "32px",
            right: `${cardConfig.right + cardConfig.width + 32}px`,
            zIndex: 2,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            opacity: isOrderFormActive ? 1 : 0,
            transform: isOrderFormActive ? "scale(1)" : "scale(0.96)",
            transition: "opacity 0.4s ease, transform 0.4s ease, right 0.4s ease",
            pointerEvents: isOrderFormActive ? "auto" : "none",
          }}
        >
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              // PdfCanvasViewer regelt zijn eigen laad-/fade-status; deze
              // overlay is alleen nog voor de "geen orderform"-melding.
              opacity: orderFormMissing ? 1 : 0,
              transition: "opacity 0.25s ease",
              pointerEvents: "none",
            }}
          >
            <Typography sx={{ color: "rgba(255,255,255,0.8)" }}>
              Failed to load PDF
            </Typography>
          </Box>

          {!orderFormMissing && (
            <Box
              sx={{
                position: "relative",
                height: "100%",
                width: "100%",
                maxWidth: "950px",
                borderRadius: "8px",
                // Alleen de navigatie-fade (isTransitioning), niet
                // isContentLoading: die gaat ook aan bij in-/uitklappen
                ...(PDF_FADE_WITH_DRAWER && (!PDF_FLIP || pdfFlipPhase === "idle") && {
                  opacity: isTransitioning ? 0 : 1,
                  transition: `opacity ${fadeDuration} ${fadeTimingFunction}`,
                }),
              }}
            >
              <PdfCanvasViewer viewer={orderFormPdfViewer} suppressSpinner={contentOpacity === 0} flip={orderFormFlip} />
            </Box>
          )}
        </Box>

        {/* ✅ FIXED: WRAPPER VOOR 3D VIEWER */}
        {/*
      <Viewer3D
          upperFileLink={upperFileLink}
          lowerFileLink={lowerFileLink}
          cardRef={cardRef}
        /> 
         
<td style={{ 
  fontFamily: 'monospace', 
  whiteSpace: 'pre-wrap', 
  fontSize: '0.9em',
  lineHeight: '1.3'
}}>
  {JSON.stringify(rowData.worktypes, null, 2)}
</td>*/}

        <Card
          ref={cardRef} // ← DIT ONDERSTEUNT
          sx={{
            position: "absolute",
            bottom: cardConfig.bottom,
            right: cardConfig.right,
            width: cardConfig.width,
            height: cardConfig.height,
            backgroundColor: "rgba(255, 255, 255, 0.10)",
            border: "1px solid rgba(255, 255, 255, 0.10)",
            borderRadius: expanded ? "0 8px 8px 0" : "8px",
            backdropFilter: expanded ? "blur(48px)" : "blur(12px)",
            WebkitBackdropFilter: expanded ? "blur(48px)" : "blur(12px)",
            transition: "all 0.4s ease",
            display: "flex",
            flexDirection: "column",
            zIndex: 10,
            overflow: "hidden",
            boxShadow: expanded
              ? "0 8px 24px rgba(0,0,0,0.5)"
              : "0 4px 12px rgba(0,0,0,0.2)",
            "&::before": {
              content: '""',
              position: "absolute",
              top: 0,
              right: 0,
              width: "100%",
              height: "100%",
              // Browser kiest zelf 1x/2x/3x/4x; url() en -webkit-image-set
              // als terugval voor oudere browsers. Via @supports i.p.v. een
              // array: in sx betekent een array breakpoints, geen terugval.
              backgroundImage: `url(${imageSet.src})`,
              "@supports (background-image: -webkit-image-set(url(x) 1x))": {
                backgroundImage: `-webkit-image-set(${bgImageSetList})`,
              },
              "@supports (background-image: image-set(url(x) 1x))": {
                backgroundImage: `image-set(${bgImageSetList})`,
              },
              backgroundRepeat: "no-repeat",
              backgroundPosition: "top right",
              backgroundSize: expanded ? "416px 288px" : "208px 144px",
              opacity: contentOpacity * 0.4,
              transition: `opacity ${fadeDuration} ${fadeTimingFunction}, background-size 0.4s ease`,
              zIndex: -1,
              pointerEvents: "none",
            },
          }}
        >
          <CardContent
            sx={{
              flex: 1,
              opacity: contentOpacity,
              // transition: `opacity ${fadeDuration} ${fadeTimingFunction}`,
              // current padding values
              paddingTop: expanded ? 4 : 2, // pt: 4 → 16px, pt: 2 → 8px
              paddingLeft: expanded ? 4.5 : 2.5, // pl: 4.5 → 18px, pl: 2.5 → 10px
              // transition: "padding-top 0.3s ease, padding-left 0.3s ease",
              transition: `opacity ${fadeDuration} ${fadeTimingFunction}, padding-top 0.3s ease, padding-left 0.3s ease`,
              display: "flex",
              flexDirection: "column",
              position: "relative",
              overflow: "hidden",
              userSelect: "none",
            }}
          >
            {/* ✅ FIXED HEADER SECTION */}
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                flexShrink: 0,
                height: 435,
              }}
            >
              {/* Patient Name */}
              <Typography
                sx={{
                  color: "#FFF",
                  fontFamily: "'DM Sans', sans-serif",
                  userSelect: "text",
                  fontSize: expanded ? 64 : 40,
                  fontStyle: "normal",
                  fontWeight: 500,
                  lineHeight: expanded ? "97%" : "40px",
                  letterSpacing: "0px",
                  whiteSpace: "pre-line",
                  transition:
                    "font-size 0.4s ease, line-height 0.4s ease, padding-bottom 0.4s ease",
                  display: "-webkit-box",
                  WebkitLineClamp: 2,
                  minHeight: expanded ? 124 : 80,
                  WebkitBoxOrient: "vertical",
                  textOverflow: "ellipsis",
                  pb: expanded ? 5.5 : 2,
                }}
              >
                {(() => {
                  // Extraheer datum en ID uit patientName en verwijder ze
                  const dateMatch =
                    rowData.patientName.match(/\d{1,2}-\d{2}-\d{4}/);
                  const idMatch = rowData.patientName.match(/ID[:;]\s*(\d+)/i); // Case-insensitive, capturet het nummer
                  const extractedId = idMatch ? idMatch[1] : null; // Alleen het nummer, bijv. "23170506"
                  const dateOfBirth = dateMatch
                    ? dateMatch[0]
                    : rowData.dateOfBirth;
                  let name = rowData.patientName
                    .replace(/\d{1,2}-\d{2}-\d{4}/g, "") // Verwijder datums
                    .replace(/ID[:;]\s*\d+/gi, "") // Verwijder ID-patronen (inclusief tikfout ;)
                    .replace("()", "")
                    .trim();
                  const nameParts = name.split(" ");
                  // Formateer de naam
                  let formattedName = "";
                  if (nameParts.length === 1) {
                    formattedName = nameParts[0];
                  } else {
                    const first = nameParts.shift();
                    let rest = nameParts.join(" ");
                    // Controleer op koppelteken in eerste 15 karakters
                    if (rest.includes("-")) {
                      const hyphenIndex = rest.indexOf("-");
                      if (hyphenIndex > -1 && hyphenIndex < 15) {
                        const beforeHyphen = rest.substring(0, hyphenIndex + 1);
                        const afterHyphen = rest
                          .substring(hyphenIndex + 1)
                          .trim();
                        formattedName = `${first} ${beforeHyphen}\n${afterHyphen}`;
                      } else if (rest.length > 15) {
                        const restWords = rest.split(" ");
                        const firstLast = restWords.shift();
                        const remainingLast = restWords.join(" ");
                        formattedName = `${first} ${firstLast}\n${remainingLast}`;
                      } else {
                        formattedName = `${first}\n${rest}`;
                      }
                    } else if (rest.length > 15) {
                      const restWords = rest.split(" ");
                      const firstLast = restWords.shift();
                      const remainingLast = restWords.join(" ");
                      formattedName = `${first} ${firstLast}\n${remainingLast}`;
                    } else {
                      formattedName = `${first}\n${rest}`;
                    }
                  }
                  return formattedName;
                })()}
                
              </Typography>
              {/* Birthday + Reference */}
              <Typography
                sx={{
                  userSelect: "text",
                  color: expanded
                    ? "rgba(255, 255, 255, 0.6)"
                    : "rgba(255, 255, 255, 0.4)",
                  fontFamily: "'DM Sans', sans-serif",
                  fontSize: 16,
                  fontWeight: 400,
                  letterSpacing: "0px",
                  position: "relative",
                  mt: 0,
                  pb: expanded ? 3 : 3,
                  minHeight: 20,
                }}
              >
                {(() => {
                  // Extraheer datum en ID uit patientName
                  const dateMatch =
                    rowData.patientName.match(/\d{1,2}-\d{2}-\d{4}/);
                  const idMatch = rowData.patientName.match(/ID[:;]\s*(\d+)/i); // Zelfde regex als hierboven
                  const extractedId = idMatch ? idMatch[1] : null;
                  let extractedDate = dateMatch ? dateMatch[0] : null;
                  if (extractedDate) {
                    extractedDate = extractedDate.replace(/-/g, "/");
                  }
                  // Gebruik geëxtraheerde datum of het aparte veld
                  const birthday = extractedDate || rowData.birthday;
                  // Gebruik ref (getrimd) of fallback naar extractedId
                  const trimmedRef = (rowData.ref || "").trim();
                  const refToUse = trimmedRef || extractedId || null;
                  if (birthday && refToUse) {
                    return `${birthday} • ${refToUse}`;
                  } else if (birthday) {
                    return birthday;
                  } else if (refToUse) {
                    return refToUse;
                  }
                  return "";
                })()}
              </Typography>
              {/* Scanner + Worktype Chips */}
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "auto 1fr",
                  gap: "2px",
                  marginLeft: localStatus == 'accepted' ? "0px" : "-6px", // Compenseert de 6px gap aan de linkerkant van de eerste chip als niet accepted
       
                  gridAutoFlow: "dense", // or remove this if you want strict left-to-right
                }}
              >
                <Box
                  sx={{
                    display: "contents", // This makes children participate in parent grid
                  }}
                >
                  <WorktypeChips
                    row={{ ...rowData, status: localStatus }}
                    worktypeMap={worktypeMap}
                    brand={rowData.scannerBrand}
                    portals={portals}
                    layoutAnimations
                  />
                </Box>
              </Box>
              {/* Dentist */}
              <Typography
                variant="subtitle2"
                sx={{
                  userSelect: "text",
                  color: "#fff",
                  fontWeight: 500,
                  fontFamily: "'DM Sans', sans-serif",
                  pt: expanded ? 3 : 2,
                  pb: 0.5,
                }}
              >
                {rowData.dentist}
              </Typography>
              {/* Clinic */}
              <Typography
                variant="subtitle2"
                sx={{
                  userSelect: "text",
                  color: expanded
                    ? "rgba(255, 255, 255, 0.6)"
                    : "rgba(255, 255, 255, 0.4)",
                  fontWeight: 400,
                  mt: -1,
                  letterSpacing: "0px",
                  fontFamily: "'DM Sans', sans-serif",
                  minHeight: 18,
                  lineHeight: 1.33,
                }}
              >
                {(() => {
                  const clinic = (rowData.clinic || "").trim();
                  const dentist = (rowData.dentist || "").trim();
                  return (
                    clinic &&
                    clinic.toLowerCase() !== dentist.toLowerCase() && (
                      <>{rowData.clinic}</>
                    )
                  );
                })()}
              </Typography>
              {/* Incoming and Delivery - Wrapped in Collapse for smooth height animation */}
              <Collapse in={expanded} timeout={400} unmountOnExit={true}>
                <>
                  {/* Divider */}
                  <Divider
                    sx={{
                      mt: 3.3,
                      mb: 4,
                      borderColor: "rgba(255, 255, 255, 0.1)",
                      width: "288px",
                    }}
                  />
                  {/* Incoming */}
                  <Box
                    sx={{
                      width: 288,
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      mb: 1,
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{
                        color: "rgba(255, 255, 255, 0.5)",
                        fontFamily: "'DM Sans', sans-serif",
                        fontSize: "14px",
                        letterSpacing: "0px",
                      }}
                    >
                      Incoming
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color: "rgba(255, 255, 255, 0.9)",
                        fontFamily: "'DM Sans', sans-serif",
                        fontSize: "14px",
                        textAlign: "right",
                      }}
                    >
                      {formatDateTimestamp(rowData.date).replace(/-/g, "/")}
                    </Typography>
                  </Box>
                  {/* Divider */}
                  <Divider
                    sx={{
                      mb: 1,
                      borderColor: "rgba(255, 255, 255, 0.1)",
                      width: "288px",
                    }}
                  />
                  {/* Delivery */}
                  <Box
                    sx={{
                      width: 288,
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{
                        color: "rgba(255, 255, 255, 0.5)",
                        fontFamily: "'DM Sans', sans-serif",
                        fontSize: "14px",
                        letterSpacing: "0px",
                      }}
                    >
                      Outgoing
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color: "rgba(255, 255, 255, 1.0)",
                        fontFamily: "'DM Sans', sans-serif",
                        fontWeight: 500,
                        fontSize: "14px",
                        textAlign: "right",
                      }}
                    >
                      {formatDeliveryDate(rowData.deliverydate) || "N/A"}
                    </Typography>
                  </Box>
                </>
              </Collapse>
            </Box>
            {/* Worktype Title with Collapse */}
            <Collapse in={expanded} timeout={200} unmountOnExit={true}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <Typography
                  sx={{
                    fontFamily: "'DM Sans', sans-serif",
                    fontSize: "20px",
                    fontWeight: 500,
                    color: "rgba(255, 255, 255 )",
                    mb: 1.5,
                    letterSpacing: "0px",
                  }}
                >
                  Worktype
                </Typography>
              </Box>
            </Collapse>
            {/* ✅ SCROLLABLE CONTENT SECTION - Using Collapse for height animation */}
            <Fade in={expanded} timeout={200} unmountOnExit={true}>
              <Box
                sx={{
                  flex: 1,
                  display: "flex",
                  flexDirection: "column",
                  gap: 2,
                  overflow: "hidden", // IMPORTANT: parent cannot scroll
                  pr: 1,
                }}
              >
                {/* ------------------------------------ */}
                {/* WORKTYPES SCROLL AREA */}
                {/* ------------------------------------ */}
                {(jawBased.length > 0 ||
                  bridges.length > 0 ||
                  singles.length > 0) && (
                    <Box

                      ref={worktypesRefCallback}
                      sx={{
                        flexShrink: 0,
                        minHeight: dynamicMinHeight,
                        maxHeight: dynamicMaxHeight,
                        overflowY: "auto",
                        overflowX: "hidden",
                        "&::-webkit-scrollbar": { display: "none" },
                        scrollbarWidth: "none",
                        msOverflowStyle: "none",
                        display: "flex",
                        flexDirection: "column",
                        gap: 0.5,
                        paddingBottom: "0px",
                        transition:
                          "mask-image 0.3s ease, -webkit-mask-image 0.3s ease",
                        maskImage:
                          worktypesAtBottom || !worktypesScrollable
                            ? "none"
                            : "linear-gradient(to bottom, rgba(0, 0, 0, 1) 80%, rgba(0, 0, 0, 0) 100%)",
                        WebkitMaskImage:
                          worktypesAtBottom || !worktypesScrollable
                            ? "none"
                            : "linear-gradient(to bottom, rgba(0, 0, 0, 1) 80%, rgba(0, 0, 0, 0) 100%)",
                      }}
                    >
                      {/* ----- JAW-BASED RESTORATIONS (Full Jaw) ----- */}
                      {jawBased.map((item, idx) => (
                        <Box
                          data-wt-row
                          key={`jaw-${idx}`}
                          sx={{
                            display: "flex",
                            width: "100%",
                            maxWidth: 304,
                            height: 48,
                            padding: 1,
                            alignItems: "center",
                            gap: 1,
                            borderRadius: "50px",
                            background:
                              "linear-gradient(90deg, rgba(255, 255, 255, 0.10) 0%, rgba(255, 255, 255, 0.00) 100%)",
                            transition: "all 0.2s ease",
                            "&:hover": {
                              background:
                                "linear-gradient(90deg, rgba(255, 255, 255, 0.10) 0%, rgba(255, 255, 255, 0.00) 100%)",
                            },
                          }}
                        >
                          {/* Jaw Icon */}
                          <Box
                            sx={{
                              width: 48,
                              height: 48,
                              borderRadius: "50%",
                              overflow: "hidden",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              backgroundColor: "rgba(255,255,255,0.2)",
                              border: "1px solid rgba(255,255,255,0.3)",
                              position: "relative",
                              flexShrink: 0,
                            }}
                          >
                            <img
                              src={
                                highlightPreviews[item.jaw]?.dataUrl ||
                                EMPTY_PIXEL
                              }
                              alt={`${item.jaw} Jaw`}
                              draggable={false}
                              onContextMenu={(e) => e.preventDefault()}
                              onDragStart={(e) => e.preventDefault()}
                              style={{
                                width: "100%",
                                height: "100%",
                                objectFit: "cover",
                              }}
                              onError={(e) => {
                                e.target.src = EMPTY_PIXEL;
                              }}
                            />
                            <Typography
                              variant="caption"
                              sx={{
                                fontSize: "10px",
                                fontFamily: "'DM Mono', monospace",
                                fontWeight: 500,
                                lineHeight: "16px",
                                color: "#fff",
                                position: "absolute",
                                top: "50%",
                                left: "50%",
                                transform: "translate(-50%, -50%)",
                                userSelect: "none",
                                zIndex: 1,
                              }}
                            >
                              {item.jaw}
                            </Typography>
                          </Box>
                          {/* Jaw Worktype Info */}
                          <Box
                            sx={{
                              display: "flex",
                              flexDirection: "column",
                              flex: 1,
                              minWidth: 0,
                            }}
                          >
                            <Typography
                              sx={{
                                color: "#FFF",
                                fontFamily: "'DM Sans', sans-serif",
                                fontSize: "14px",
                                fontWeight: 500,
                                lineHeight: "16px",
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                              }}
                            >
                              {[getWorktypeDisplayName(item.worktype)]
                                .filter(Boolean)
                                .join(" ")}
                            </Typography>
                            <Typography
                              sx={{
                                color: "rgba(255, 255, 255, 0.6)",
                                fontFamily: "'DM Sans', sans-serif",
                                fontSize: "12px",
                                fontWeight: 400,
                                lineHeight: "16px",
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                              }}
                            >
                              {[
                                item.worktype.MaterialID,
                                item.worktype.SettingID,
                                item.worktype.Shade,
                              ]
                                .filter(Boolean)
                                .join(" • ")}
                            </Typography>
                          </Box>
                        </Box>
                      ))}

                      {/* ----- BRIDGE RESTORATIONS ----- */}
                      {bridges.map((bridge, bridgeIdx) => (
                        <Box
                          key={`bridge-${bridgeIdx}`}
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            gap: 0.1,
                          }}
                        >
                          {/* Bridge Header */}
                          <Box
                            sx={{
                              padding: "0 16px",
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                            }}
                          >
                            <Typography
                              sx={{
                                fontFamily: "'DM Mono', monospace",
                                fontSize: "11px",
                                fontWeight: 600,
                                color: "rgba(255, 255, 255, 0.6)",
                                textTransform: "uppercase",
                                letterSpacing: "0.5px",
                              }}
                            >
                              {(bridge?.worktype?.RestorationTypeID || "").replace(/([A-Z])/g, " $1").trim()}{" "}
                              {bridge?.fromTo || ""}
                            </Typography>
                          </Box>
                          {/* Bridge Units */}
                          {bridge?.units?.map((unit) => (
                            <Box
                              data-wt-row
                              key={`unit-${unit.num}`}
                              sx={{
                                display: "flex",
                                width: "100%",
                                maxWidth: 304,
                                height: 48,
                                padding: 1,
                                alignItems: "center",
                                gap: 1,
                                borderRadius: "50px",
                                background:
                                  "linear-gradient(90deg, rgba(255, 255, 255, 0.10) 0%, rgba(255, 255, 255, 0.00) 100%)",
                                transition: "all 0.2s ease",
                                "&:hover": {
                                  background:
                                    "linear-gradient(90deg, rgba(255, 255, 255, 0.10) 0%, rgba(255, 255, 255, 0.00) 100%)",
                                },
                                ml: 1,
                              }}
                            >
                              {/* Tooth Image */}
                              <Box
                                sx={{
                                  width: 48,
                                  height: 48,
                                  borderRadius: "50%",
                                  overflow: "hidden",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  backgroundColor: "rgba(255,255,255,0.2)",
                                  border: "1px solid rgba(255,255,255,0.3)",
                                  position: "relative",
                                  flexShrink: 0,
                                }}
                              >
                                <img
                                  src={unit.preview?.dataUrl || EMPTY_PIXEL}
                                  alt={`Tooth ${unit.num}`}
                                  style={{
                                    width: "100%",
                                    height: "100%",
                                    objectFit: "cover",
                                  }}
                                  onError={(e) => {
                                    e.target.src = EMPTY_PIXEL;
                                  }}
                                />
                                <Typography
                                  variant="caption"
                                  sx={{
                                    fontSize: "10px",
                                    fontFamily: "'DM Mono', monospace",
                                    fontWeight: 500,
                                    lineHeight: "16px",
                                    color: "#000",
                                    position: "absolute",
                                    top: "50%",
                                    left: "50%",
                                    transform: "translate(-50%, -50%)",
                                    zIndex: 1,
                                  }}
                                >
                                  {unit.num}
                                </Typography>
                              </Box>
                              {/* Unit Worktype Info */}
                              {unit?.preview?.worktype && (
                                <Box
                                  sx={{
                                    display: "flex",
                                    flexDirection: "column",
                                    flex: 1,
                                    minWidth: 0,
                                  }}
                                >
                                  <Typography
                                    sx={{
                                      color: "#FFF",
                                      fontFamily: "'DM Sans', sans-serif",
                                      fontSize: "14px",
                                      fontWeight: 500,
                                      lineHeight: "16px",
                                      whiteSpace: "nowrap",
                                      overflow: "hidden",
                                      textOverflow: "ellipsis",
                                    }}
                                  >
                                    {getBridgeUnitName(unit.preview.worktype)}
                                  </Typography>
                                  <Typography
                                    sx={{
                                      color: "rgba(255, 255, 255, 0.6)",
                                      fontFamily: "'DM Sans', sans-serif",
                                      fontSize: "12px",
                                      fontWeight: 400,
                                      lineHeight: "16px",
                                      whiteSpace: "nowrap",
                                      overflow: "hidden",
                                      textOverflow: "ellipsis",
                                    }}
                                  >
                                    {[
                                      getMaterialName(unit.preview.worktype),
                                      unit.preview.worktype.SettingID,
                                      unit.preview.worktype.Shade,
                                    ]
                                      .filter(Boolean)
                                      .join(" • ")}
                                  </Typography>
                                </Box>
                              )}
                            </Box>
                          ))}
                        </Box>
                      ))}

                      {/* ----- SINGLE RESTORATIONS ----- */}
                      {singles.map(({ num, preview, worktype }) => (
                        <Box
                          data-wt-row
                          key={`single-${num}`}
                          sx={{
                            display: "flex",
                            width: "100%",
                            maxWidth: 304,
                            height: 48,
                            padding: 1,
                            alignItems: "center",
                            gap: 1,
                            borderRadius: "50px",
                            background:
                              "linear-gradient(90deg, rgba(255, 255, 255, 0.10) 0%, rgba(255, 255, 255, 0.00) 100%)",
                            transition: "all 0.2s ease",
                            "&:hover": {
                              background:
                                "linear-gradient(90deg, rgba(255, 255, 255, 0.10) 0%, rgba(255, 255, 255, 0.00) 100%)",
                            },
                          }}
                        >
                          {/* Tooth Image */}
                          <Box
                            sx={{
                              width: 48,
                              height: 48,
                              borderRadius: "50%",
                              overflow: "hidden",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              backgroundColor: "rgba(255,255,255,0.2)",
                              border: "1px solid rgba(255,255,255,0.3)",
                              position: "relative",
                              flexShrink: 0,
                            }}
                          >
                            <img
                              src={preview?.dataUrl || EMPTY_PIXEL}
                              alt={`Tooth ${num}`}
                              draggable={false}
                              onContextMenu={(e) => e.preventDefault()}
                              onDragStart={(e) => e.preventDefault()}
                              style={{
                                width: "100%",
                                height: "100%",
                                objectFit: "cover",
                              }}
                              onError={(e) => {
                                e.target.src = EMPTY_PIXEL;
                              }}
                            />
                            <Typography
                              variant="caption"
                              sx={{
                                fontSize: "10px",
                                fontFamily: "'DM Mono', monospace",
                                fontWeight: 500,
                                lineHeight: "16px",
                                color: "#000",
                                position: "absolute",
                                top: "50%",
                                left: "50%",
                                transform: "translate(-50%, -50%)",
                                userSelect: "none",
                                zIndex: 1,
                              }}
                            >
                              <div
                                style={{ color: !isNaN(num) ? "#000" : "#fff" }}
                              >
                                {num}
                              </div>
                            </Typography>
                          </Box>
                          {/* Worktype Info */}
                          {worktype && (
                            <Box
                              sx={{
                                display: "flex",
                                flexDirection: "column",
                                flex: 1,
                                minWidth: 0,
                              }}
                            >
                              <Typography
                                sx={{
                                  color: "#FFF",
                                  fontFamily: "'DM Sans', sans-serif",
                                  fontSize: "14px",
                                  fontWeight: 500,
                                  lineHeight: "16px",
                                  whiteSpace: "nowrap",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                }}
                              >
                                {[getWorktypeDisplayName(worktype)]
                                  .filter(Boolean)
                                  .join(" ")}
                              </Typography>
                              <Typography
                                sx={{
                                  color: "rgba(255, 255, 255, 0.6)",
                                  fontFamily: "'DM Sans', sans-serif",
                                  fontSize: "12px",
                                  fontWeight: 400,
                                  lineHeight: "16px",
                                  whiteSpace: "nowrap",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                }}
                              >
                                {[
                                  getMaterialName(worktype),
                                  worktype.SettingID,
                                  worktype.Shade,
                                ]
                                  .filter(Boolean)
                                  .join(" • ")}
                              </Typography>
                            </Box>
                          )}
                        </Box>
                      ))}
                    </Box>
                  )}
                {/* ------------------------------------ */}
                {/* NOTES SCROLL AREA */}
                {/* ------------------------------------ */}
                {rowData.remark && (
                  <Box
                    sx={{
                      flex: 1,
                      overflow: "hidden", // parent not scrollable
                      display: "flex",
                      flexDirection: "column",
                      maxWidth: 310,
                      maxHeight: notesAllocated != null ? `${notesAllocated}px` : "none",
                    }}
                  >
                    {/* FIXED HEADER (not scrolling) */}
                    <Typography
                      ref={notesHeaderRef}
                      variant="subtitle2"
                      sx={{
                        fontFamily: "'DM Sans', sans-serif",
                        fontWeight: 500,
                        color: "rgba(255, 255, 255)",
                        fontSize: "20px",
                        flexShrink: 0,
                        mb: 1,
                      }}
                    >
                      Note
                    </Typography>
                    {/* SCROLLABLE NOTE CONTENT */}
                    <Box
                      ref={notesRef}
                      sx={{
                        flex: 1,
                        overflowY: "auto",
                        overflowX: "hidden",
                        "&::-webkit-scrollbar": { display: "none" },
                        scrollbarWidth: "none",
                        msOverflowStyle: "none",
                        paddingBottom: "0px",
                        transition: "mask-image 0.3s ease, -webkit-mask-image 0.3s ease",
                        maskImage:
                          notesAtBottom || !notesScrollable
                            ? "none"
                            : "linear-gradient(to bottom, rgba(0, 0, 0, 1) 80%, rgba(0, 0, 0, 0) 100%)",
                        WebkitMaskImage:
                          notesAtBottom || !notesScrollable
                            ? "none"
                            : "linear-gradient(to bottom, rgba(0, 0, 0, 1) 80%, rgba(0, 0, 0, 0) 100%)",
                      }}
                    >
                      <Typography
                        ref={notesContentRef}
                        variant="body2"
                        sx={{
                          borderLeft: "1px solid rgba(255, 255, 255, 0.1)",
                          fontFamily: "'DM Sans', sans-serif",
                          fontWeight: 400,
                          color: "rgba(255, 255, 255, 0.6)",
                          fontSize: "14px",
                          lineHeight: "125%",
                          whiteSpace: "pre-wrap",
                          pl: 1,
                          pr: 1,
                          userSelect: "text",
                        }}
                      >
                        {rowData.remark
                          .replace(/^"|"$/g, "")
                          .replace(/\\n/g, "\n")
                          .replace(/&gt;/g, ">")
                          .replace(/&lt;/g, "<")
                          .replace(/&amp;/g, "&")}
                      </Typography>
                    </Box>
                  </Box>
                )}
              </Box>
            </Fade>
          </CardContent>
          {/* Action Buttons */}
          <Box
            sx={{
              position: "absolute",
              top: expanded ? 264 : "auto",
              bottom: expanded ? "auto" : 16,
              left: expanded ? 456 - caseSummaryShift : 16,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              transition: `top 0.4s ease, bottom 0.4s ease, left 0.4s ease`,
              userSelect: "none",
              opacity: contentOpacity,
              zIndex: 12,
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                width: "48px",
                height: "48px",
                borderRadius: "50%",
                border: "1px solid rgba(255,255,255,0.10)",
                backdropFilter: "blur(4px)",
                cursor: "pointer",
                "&:hover": {
                  background: "rgba(255,255,255,0.2)",
                },
              }}
              onClick={() => {
                if (!isDownloading && rowData?.orderform) {
                  onDownloadPdf(rowData);
                } else if (!rowData?.orderform) {
                  alert("No PDF available for this case.");
                }
              }}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (
                  (e.key === "Enter" || e.key === " ") &&
                  !isDownloading &&
                  rowData?.orderform
                ) {
                  e.preventDefault();
                  onDownloadPdf(rowData);
                }
              }}
            >
              <img
                src={PdfIcon}
                alt="PDF"
                draggable={false}
                onContextMenu={(e) => e.preventDefault()}
                onDragStart={(e) => e.preventDefault()}
                style={{
                  userSelect: "none",
                  width: 16,
                  height: 16,
                  objectFit: "contain",
                }}
              />
            </Box>

            {expanded && (
              <Typography
                sx={{
                  color: "#FFF",
                  textAlign: "center",
                  fontFamily: "DM Sans",
                  fontSize: "14px",
                  fontStyle: "normal",
                  fontWeight: 500,
                  lineHeight: "16px",
                  mt: "6px",
                }}
              >
                PDF
              </Typography>
            )}
          </Box>
          <Box
            sx={{
              position: "absolute",
              top: expanded ? 264 : "auto",
              bottom: expanded ? "auto" : 16,
              left: expanded ? 508 - caseSummaryShift : 68,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              transition: `top 0.4s ease, bottom 0.4s ease, left 0.4s ease`,
              userSelect: "none",
              opacity: isDownloading ? 0.1 : contentOpacity,
              zIndex: 12,
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                width: "48px",
                height: "48px",
                borderRadius: "50%",
                border: "1px solid rgba(255,255,255,0.10)",
                backdropFilter: "blur(4px)",
                cursor: "pointer",
                "&:hover": {
                  background: isDownloading
                    ? "rgba(255,255,255,0.1)"
                    : "rgba(255,255,255,0.2)",
                },
              }}
              onClick={() => {
                if (
                  !isDownloading &&
                  (rowData?.orderform || rowData?.stlfiles)
                ) {
                  onDownloadZipWithPdf(rowData);
                } else if (!rowData?.orderform && !rowData?.stlfiles) {
                  alert("Geen bestanden beschikbaar voor deze case.");
                }
              }}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (
                  (e.key === "Enter" || e.key === " ") &&
                  !isDownloading &&
                  (rowData?.orderform || rowData?.stlfiles)
                ) {
                  e.preventDefault();
                  onDownloadZipWithPdf(rowData);
                }
              }}
            >
              <img
                src={StlIcon}
                alt="STL"
                draggable={false}
                onContextMenu={(e) => e.preventDefault()}
                onDragStart={(e) => e.preventDefault()}
                style={{
                  userSelect: "none",
                  width: 16,
                  height: 16,
                  objectFit: "contain",
                }}
              />
            </Box>

            {expanded && (
              <Typography
                sx={{
                  color: "#FFF",
                  textAlign: "center",
                  fontFamily: "DM Sans",
                  fontSize: "14px",
                  fontStyle: "normal",
                  fontWeight: 500,
                  lineHeight: "16px",
                  mt: "6px",
                }}
              >
                STL
              </Typography>
            )}
          </Box>

          <Box
            sx={{
              position: "absolute",
              top: expanded ? 264 : "auto",
              bottom: expanded ? "auto" : 16,
              left: expanded ? 560 - caseSummaryShift : 120,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              transition: `top 0.4s ease, bottom 0.4s ease, left 0.4s ease`,
              userSelect: "none",
              opacity: isDownloading ? 0.1 : contentOpacity,
              zIndex: 12,
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                width: "48px",
                height: "48px",
                borderRadius: "50%",
                border: "1px solid rgba(255,255,255,0.10)",
                backdropFilter: "blur(4px)",
                cursor: "pointer",
                "&:hover": {
                  background: isDownloading
                    ? "rgba(255,255,255,0.1)"
                    : "rgba(255,255,255,0.2)",
                },
              }}
              onClick={() => {
                if (!isDownloading && rowData?.stlfiles) {
                  onDownloadStlZip(rowData);
                } else if (!rowData?.stlfiles) {
                  alert("Geen PLY-bestanden beschikbaar voor deze case.");
                }
              }}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (
                  (e.key === "Enter" || e.key === " ") &&
                  !isDownloading &&
                  rowData?.stlfiles
                ) {
                  e.preventDefault();
                  onDownloadStlZip(rowData);
                }
              }}
            >
              <img
                src={PlyIcon}
                alt="Scans"
                draggable={false}
                onContextMenu={(e) => e.preventDefault()}
                onDragStart={(e) => e.preventDefault()}
                style={{
                  userSelect: "none",
                  width: 16,
                  height: 16,
                  objectFit: "contain",
                }}
              />
            </Box>

            {expanded && (
              <Typography
                sx={{
                  color: "#FFF",
                  textAlign: "center",
                  fontFamily: "DM Sans",
                  fontSize: "14px",
                  fontStyle: "normal",
                  fontWeight: 500,
                  lineHeight: "16px",
                  mt: "6px",
                }}
              >
                PLY
              </Typography>
            )}
          </Box>
          {/* Eigen PDF-bon (CaseSummaryPdf), achter FEATURE_CASE_SUMMARY_PDF */}
          {FEATURE_CASE_SUMMARY_PDF() && (
            <Box
              sx={{
                position: "absolute",
                top: expanded ? 264 : "auto",
                bottom: expanded ? "auto" : 16,
                left: expanded ? 560 : 172,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                transition: `top 0.4s ease, bottom 0.4s ease, left 0.4s ease`,
                userSelect: "none",
                opacity: contentOpacity,
                zIndex: 12,
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  width: "48px",
                  height: "48px",
                  borderRadius: "50%",
                  border: "1px solid rgba(255,255,255,0.10)",
                  backdropFilter: "blur(4px)",
                  cursor: "pointer",
                  "&:hover": {
                    background: "rgba(255,255,255,0.2)",
                  },
                }}
                onClick={() => rowData && openCaseSummaryPdf({ ...rowData, status: localStatus })}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if ((e.key === "Enter" || e.key === " ") && rowData) {
                    e.preventDefault();
                    openCaseSummaryPdf({ ...rowData, status: localStatus });
                  }
                }}
              >
                <img
                  src={CadmusIcon}
                  alt="Cadmus PDF"
                  draggable={false}
                  onContextMenu={(e) => e.preventDefault()}
                  onDragStart={(e) => e.preventDefault()}
                  style={{
                    userSelect: "none",
                    width: 16,
                    height: 16,
                    objectFit: "contain",
                  }}
                />
              </Box>

              {expanded && (
                <Typography
                  sx={{
                    color: "#FFF",
                    textAlign: "center",
                    fontFamily: "DM Sans",
                    fontSize: "14px",
                    fontStyle: "normal",
                    fontWeight: 500,
                    lineHeight: "16px",
                    mt: "6px",
                  }}
                >
                  PDF
                </Typography>
              )}
            </Box>
          )}
          {/* Accept Button */}
          <AnimatePresence>
            {FEATURE_ACCEPT_ENABLED() && expanded && contentOpacity === 1 && localStatus !== 'accepted' && (
              <motion.div
                initial={{ opacity: 0, scale: 0.5 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.5 }}
                transition={{ type: "spring", stiffness: 500, damping: 30 }}
                onMouseEnter={() => {
                  acceptHoverTimer.current = setTimeout(() => setAcceptHovered(true), 350);
                }}
                onMouseLeave={() => {
                  clearTimeout(acceptHoverTimer.current);
                  setAcceptHovered(false);
                }}
                style={{
                  position: "absolute",
                  bottom: 16,
                  right: acceptHovered ? 32 : 88,
                  zIndex: 12,
                  transition: "right 0.44s cubic-bezier(0.34, 1.3, 0.64, 1)",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "row",
                    justifyContent: "center",
                    alignItems: "center",
                    gap: acceptHovered ? "6px" : "0px",
                    width: acceptHovered ? "160px" : "48px",
                    height: "48px",
                    borderRadius: acceptHovered ? "24px" : "50%",
                    border: acceptHovered ? "1.5px solid #1D9E75" : "1px solid rgba(255,255,255,0.10)",
                    background: acceptHovered ? "rgba(29,158,117,0.12)" : "rgba(255,255,255,0.18)",
                    boxShadow: acceptHovered ? "0 0 0 2px rgba(29,158,117,0.15), 0 0 10px 2px rgba(29,158,117,0.25)" : "none",
                    cursor: "pointer",
                    overflow: "hidden",
                    transition: [
                      "width 0.44s cubic-bezier(0.34, 1.3, 0.64, 1)",
                      "border-radius 0.44s cubic-bezier(0.34, 1.3, 0.64, 1)",
                      "border 0.28s ease",
                      "background 0.28s ease",
                      "box-shadow 0.28s ease",
                    ].join(", "),
                  }}
                  onClick={async (e) => {
                    e.stopPropagation();
                    if (!rowData) return;
                    const newStatus = 'accepted';
                    setLocalStatus(newStatus);
                    clearTimeout(acceptHoverTimer.current);
                    setAcceptHovered(false);
                    try {
                      await apiClient.patch(`/cases/${rowData.id}/status`, { status: newStatus });
                      onUpdateRow?.(rowData.id, { status: newStatus, flag: 1 });
                      if (rowData.configuredPortalId) {
                        apiClient.post('/processing_queue', { configured_portal_id: rowData.configuredPortalId, external_id: rowData.externalId, action: 'status', value: newStatus }).catch(console.error);
                      }
                    } catch (err) {
                      console.error("Failed to update status:", err);
                      setLocalStatus(localStatus);
                    }
                  }}
                >
                  <CheckIcon sx={{
                    fontSize: 18,
                    color: '#FFFFFF',
                    flexShrink: 0,
                  }} />
                  <Box
                    component="span"
                    sx={{
                      fontSize: "14px",
                      fontWeight: 500,
                      color: "#FFFFFF",
                      letterSpacing: "0.02em",
                      whiteSpace: "nowrap",
                      opacity: acceptHovered ? 1 : 0,
                      maxWidth: acceptHovered ? "70px" : "0px",
                      transition: [
                        "opacity 0.22s ease 0.14s",
                        "max-width 0.44s cubic-bezier(0.34, 1.3, 0.64, 1)",
                      ].join(", "),
                      overflow: "hidden",
                    }}
                  >
                    ACCEPT
                  </Box>
                </Box>
              </motion.div>
            )}
          </AnimatePresence>
          {/* Expand / Collapse Button */}
          <Box
            sx={{
              position: "absolute",
              bottom: 16,
              right: 32,
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              width: "48px",
              height: "48px",
              borderRadius: "50%",
              border: "1px solid rgba(255,255,255,0.10)",
              background: "rgba(255,255,255,0.10)",
              backdropFilter: "blur(4px)",
              cursor: "pointer",
              transition: [
                "opacity 0.22s ease",
                "transform 0.38s cubic-bezier(0.34, 1.3, 0.64, 1)",
                "background 0.3s ease",
              ].join(", "),
              opacity: acceptHovered ? 0 : 1,
              transform: acceptHovered ? "scale(0.5)" : "scale(1)",
              pointerEvents: acceptHovered ? "none" : "auto",
              "&:hover": { background: "rgba(255,255,255,0.2)" },
              zIndex: 12,
            }}
            onClick={(e) => {
              e.stopPropagation();
              const next = !expanded;
              setExpanded(next);
              localStorage.setItem("viewer_expanded", String(next));
            }}
          >
            {expanded ? (
              <img
                src={ContractIcon}
                alt="Collapse"
                draggable={false}
                onContextMenu={(e) => e.preventDefault()}
                onDragStart={(e) => e.preventDefault()}
                style={{
                  width: 14,
                  height: 14,
                  objectFit: "contain",
                  userSelect: "none",
                  WebkitUserSelect: "none",
                  MozUserSelect: "none",
                  MsUserSelect: "none",
                  pointerEvents: "none",
                }}
              />
            ) : (
              <img
                src={ExpandIcon}
                alt="Expand"
                draggable={false}
                onContextMenu={(e) => e.preventDefault()}
                onDragStart={(e) => e.preventDefault()}
                style={{
                  width: 14,
                  height: 14,
                  objectFit: "contain",
                  userSelect: "none",
                  WebkitUserSelect: "none",
                  MozUserSelect: "none",
                  MsUserSelect: "none",
                  pointerEvents: "none",
                }}
              />
            )}
          </Box>
          {/* DentalChart */}
          <Box
            sx={{
              position: "absolute",
              top: 355,
              right: 32,
              width: 256,
              height: 400,
              transform: expanded ? "scale(1)" : "scale(0)",
              opacity: expanded ? contentOpacity : 0,
              transformOrigin: "bottom right",
              transition: expanded
                ? `transform 0.4s cubic-bezier(0.4, 0, 0.2, 1), opacity ${fadeDuration} ${fadeTimingFunction} 0.0s`
                : `transform 0.4s cubic-bezier(0.4, 0, 0.2, 1), opacity ${fadeDuration} ${fadeTimingFunction}`,
              zIndex: 11,
              pointerEvents: "none",
            }}
          >
            <DentalChart
              ref={chartRef}
              style={{
                width: "100%",
                height: "100%",
              }}
              initialHighlights={generateDentalHighlights(rowData.worktypes)}
            />
          </Box>

          {/* Debug overlay: available space + needed breakdown */}
          {/* {measureOverlay && (
            <>
              <Box sx={{
                position: "absolute", top: measureOverlay.top, left: 0,
                width: 4, height: measureOverlay.height,
                background: "rgba(255, 80, 80, 0.7)",
                zIndex: 99, pointerEvents: "none",
              }} />
              <Box sx={{
                position: "absolute", top: measureOverlay.top, left: 4,
                width: 4, height: Math.min(measureOverlay.worktypesNeeded, measureOverlay.height),
                background: "rgba(80, 160, 255, 0.8)",
                zIndex: 99, pointerEvents: "none",
              }} />
              <Box sx={{
                position: "absolute",
                top: measureOverlay.top + Math.min(measureOverlay.worktypesNeeded, measureOverlay.height),
                left: 4,
                width: 4,
                height: Math.min(measureOverlay.notesNeeded, measureOverlay.height - Math.min(measureOverlay.worktypesNeeded, measureOverlay.height)),
                background: "rgba(80, 255, 160, 0.8)",
                zIndex: 99, pointerEvents: "none",
              }} />
              <Box sx={{
                position: "absolute", top: measureOverlay.top + 8, left: 12,
                background: "rgba(0,0,0,0.75)", color: "#fff",
                fontSize: "11px", fontFamily: "monospace", lineHeight: 1.6,
                px: 0.75, py: 0.5, borderRadius: 1,
                zIndex: 99, pointerEvents: "none",
              }}>
                <div style={{ color: "#ff6060" }}>avail: {Math.round(measureOverlay.height)}px</div>
                <div style={{ color: "#50a0ff" }}>wt needed: {Math.round(measureOverlay.worktypesNeeded)}px</div>
                <div style={{ color: "#50ffa0" }}>note hdr: {Math.round(measureOverlay.notesHeaderHeight)}px + content: {Math.round(measureOverlay.notesContentNeeded)}px = {Math.round(measureOverlay.notesNeeded)}px</div>
                <div style={{ color: "#ffcc00" }}>total needed: {Math.round(measureOverlay.worktypesNeeded + measureOverlay.notesNeeded)}px</div>
              </Box>
            </>
          )} */}
        </Card>
      </Box>
    </Drawer>
  );
}
